// @generated

import { z } from 'zod';

export type GetWebSessionBody = z.infer<typeof GetWebSessionBody>;
export const GetWebSessionBody = z.object({
	token: z.string(),
});

export type SuccessResponseDto = z.infer<typeof SuccessResponseDto>;
export const SuccessResponseDto = z.object({
	success: z.coerce.boolean().optional(),
});

export type HTTPError = z.infer<typeof HTTPError>;
export const HTTPError = z.object({
	displayMessage: z.string(),
	fields: z
		.union([
			z.array(
				z.object({
					field: z.string(),
					displayMessage: z.string(),
				}),
			),
			z.undefined(),
		])
		.optional(),
});

export type LoginBody = z.infer<typeof LoginBody>;
export const LoginBody = z.object({
	email: z.string(),
	password: z.string(),
});

export type TroonCardSubscriptionProductType = z.infer<typeof TroonCardSubscriptionProductType>;
export const TroonCardSubscriptionProductType = z.union([
	z.literal('TROON_ACCESS'),
	z.literal('TROON_ACCESS_PLUS'),
	z.literal('TROON_ACCESS_ELITE'),
]);

export type TroonRewardsLevel = z.infer<typeof TroonRewardsLevel>;
export const TroonRewardsLevel = z.union([
	z.literal('MEMBER'),
	z.literal('SILVER'),
	z.literal('GOLD'),
	z.literal('PLATINUM'),
]);

export type AccessRewardsTierType = z.infer<typeof AccessRewardsTierType>;
export const AccessRewardsTierType = z.union([
	z.literal('BASE'),
	z.literal('SILVER'),
	z.literal('GOLD'),
	z.literal('PLATINUM'),
]);

export type AuthUser = z.infer<typeof AuthUser>;
export const AuthUser = z.object({
	id: z.string(),
	email: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	troonAccessProductType: z.union([TroonCardSubscriptionProductType, z.null()]),
	phoneNumber: z.union([z.string(), z.null()]),
	hasShopifyAccount: z.coerce.boolean(),
	troonRewardsId: z.string(),
	rewardsLevel: TroonRewardsLevel,
	rewardsPoints: z.coerce.number(),
	referralCode: z.string(),
	zipcode: z.string(),
	supportsTroonRewards: z.coerce.boolean(),
	accessRewardsOptIn: z.coerce.boolean(),
	accessConversionOverrideEnabled: z.coerce.boolean(),
	accessRewardsBalance: z.union([
		z.object({
			availablePoints: z.coerce.number(),
			pendingPoints: z.coerce.number(),
			lifetimeEarnedPoints: z.coerce.number(),
			lifetimeRedeemedPoints: z.coerce.number(),
			currentTierPoints: z.coerce.number(),
			currentTier: AccessRewardsTierType,
			tierOverride: z.union([AccessRewardsTierType, z.null()]),
		}),
		z.null(),
	]),
});

export type LoginResponse = z.infer<typeof LoginResponse>;
export const LoginResponse = z.object({
	accessToken: z.string(),
	refreshToken: z.string(),
	user: AuthUser,
});

export type ProgramSignUpType = z.infer<typeof ProgramSignUpType>;
export const ProgramSignUpType = z.union([
	z.literal('BAYCLUB'),
	z.literal('VILLAGE_GAINEY'),
	z.literal('VILLAGE_CAMELBACK'),
	z.literal('VILLAGE_DC_RANCH'),
	z.literal('VILLAGE_OCOTILLO'),
]);

export type SignupBody = z.infer<typeof SignupBody>;
export const SignupBody = z.object({
	email: z.string(),
	password: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	zipcode: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	embedFacilitySlug: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	programSignUpType: z.union([ProgramSignUpType, z.undefined()]).optional(),
	supportsTroonRewards: z.union([z.coerce.boolean(), z.undefined()]).optional(),
});

export type SignupResponse = z.infer<typeof SignupResponse>;
export const SignupResponse = z.object({
	user: AuthUser,
	accessToken: z.string(),
	refreshToken: z.string(),
});

export type RefreshTokenBody = z.infer<typeof RefreshTokenBody>;
export const RefreshTokenBody = z.object({
	refreshToken: z.string(),
});

export type RefreshTokenResponse = z.infer<typeof RefreshTokenResponse>;
export const RefreshTokenResponse = z.object({
	accessToken: z.string(),
	refreshToken: z.string(),
	user: AuthUser,
});

export type EmailMagicLinkBody = z.infer<typeof EmailMagicLinkBody>;
export const EmailMagicLinkBody = z.object({
	emailOrRewardsId: z.string(),
	redirectUrl: z.union([z.string(), z.undefined()]).optional(),
});

export type ConfirmMagicLinkBody = z.infer<typeof ConfirmMagicLinkBody>;
export const ConfirmMagicLinkBody = z.object({
	emailOrRewardsId: z.string(),
	confirmationCode: z.string(),
});

export type ForgotPasswordBody = z.infer<typeof ForgotPasswordBody>;
export const ForgotPasswordBody = z.object({
	emailOrRewardsId: z.string(),
	redirectUrl: z.union([z.string(), z.undefined()]).optional(),
});

export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponse>;
export const ForgotPasswordResponse = z.object({
	emailOrRewardsId: z.string(),
	expirationInMinutes: z.coerce.number(),
});

export type ResetPasswordBody = z.infer<typeof ResetPasswordBody>;
export const ResetPasswordBody = z.object({
	emailOrRewardsId: z.string(),
	password: z.string(),
	confirmationCode: z.string(),
});

export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponse>;
export const ResetPasswordResponse = z.object({
	success: z.coerce.boolean(),
});

export type DeviceType = z.infer<typeof DeviceType>;
export const DeviceType = z.union([z.literal('IOS'), z.literal('ANDROID'), z.literal('WEB')]);

export type LogoutBody = z.infer<typeof LogoutBody>;
export const LogoutBody = z.object({
	deviceType: DeviceType.optional(),
	deviceId: z.string().optional(),
});

export type LogoutResponse = z.infer<typeof LogoutResponse>;
export const LogoutResponse = z.object({
	success: z.coerce.boolean(),
});

export type UpdateMeBody = z.infer<typeof UpdateMeBody>;
export const UpdateMeBody = z.object({
	supportsTroonRewards: z.coerce.boolean().optional(),
});

export type ConversionOption = z.infer<typeof ConversionOption>;
export const ConversionOption = z.object({
	label: z.string(),
	description: z.string(),
	conversionRatio: z.string(),
	currentPoints: z.coerce.number(),
	convertedPoints: z.coerce.number(),
	requiresAccessUpgrade: z.coerce.boolean(),
	effectiveTier: z.union([z.literal('BASE'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
	needsTierOverride: z.coerce.boolean(),
	tierOverride: z.union([
		z.union([z.literal('BASE'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
		z.null(),
	]),
});

export type PointsConversionPreview = z.infer<typeof PointsConversionPreview>;
export const PointsConversionPreview = z.object({
	currentTroonRewardsPoints: z.coerce.number(),
	currentTroonRewardsLevel: z.union([
		z.literal('MEMBER'),
		z.literal('SILVER'),
		z.literal('GOLD'),
		z.literal('PLATINUM'),
	]),
	hasAccessMembership: z.coerce.boolean(),
	currentAccessTier: z.union([
		z.union([z.literal('BASE'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
		z.null(),
	]),
	primaryConversion: ConversionOption,
	upgradeConversion: z.union([ConversionOption, z.null()]),
	upgradeAdditionalPoints: z.union([z.coerce.number(), z.null()]),
	canConvert: z.coerce.boolean(),
	message: z.union([z.string(), z.null()]),
});

export type PointsConversionConfirmResponse = z.infer<typeof PointsConversionConfirmResponse>;
export const PointsConversionConfirmResponse = z.object({
	oldTroonRewardsPoints: z.coerce.number(),
	newAccessRewardsPoints: z.coerce.number(),
	conversionRatio: z.string(),
	transactionId: z.union([z.string(), z.null()]),
	message: z.string(),
});

export type DayCreated = z.infer<typeof DayCreated>;
export const DayCreated = z.object({
	year: z.coerce.number(),
	month: z.coerce.number(),
	day: z.coerce.number(),
});

export type UserTransaction = z.infer<typeof UserTransaction>;
export const UserTransaction = z.object({
	points: z.coerce.number(),
	facilityName: z.union([z.string(), z.null()]),
	facilityLogo: z.union([z.string(), z.null()]),
	dayCreated: DayCreated,
	transactionType: z.union([z.literal('EARN'), z.literal('REDEEM'), z.literal('DEDUCTION')]),
	description: z.union([z.string(), z.null()]),
	state: z.union([
		z.literal('PENDING'),
		z.literal('CONFIRMED'),
		z.literal('FAILED'),
		z.literal('EXPIRED'),
		z.literal('CANCELLED'),
		z.literal('REJECTED'),
	]),
	source: z.union([z.literal('OPERATOR'), z.literal('SYSTEM'), z.literal('TROON_STORE')]),
});

export type UserTransactionsResponse = z.infer<typeof UserTransactionsResponse>;
export const UserTransactionsResponse = z.object({
	transactions: z.array(UserTransaction),
});

export type ProgramSignupInfo = z.infer<typeof ProgramSignupInfo>;
export const ProgramSignupInfo = z.object({
	name: z.string(),
	rewardsLevel: z.union([TroonRewardsLevel, z.null()]),
});

export type ProgramSignupInfoResponse = z.infer<typeof ProgramSignupInfoResponse>;
export const ProgramSignupInfoResponse = z.object({
	data: z.union([ProgramSignupInfo, z.null()]),
});

export type ProgramSignupEnrollResponse = z.infer<typeof ProgramSignupEnrollResponse>;
export const ProgramSignupEnrollResponse = z.object({
	newRewardsLevel: z.union([TroonRewardsLevel, z.null()]),
	originalRewardsLevel: TroonRewardsLevel,
});

export type AddonPreviewRequestBody = z.infer<typeof AddonPreviewRequestBody>;
export const AddonPreviewRequestBody = z.object({
	subscriptionId: z.string(),
	troonCardSubscriptionAddonId: z.string(),
	promoCode: z.union([z.string(), z.undefined()]).optional(),
});

export type LineItem = z.infer<typeof LineItem>;
export const LineItem = z.object({
	name: z.union([z.string(), z.null()]),
	price: z.coerce.number(),
	priceDisplay: z.string(),
	description: z.union([z.string(), z.null()]),
});

export type Currency = z.infer<typeof Currency>;
export const Currency = z.object({
	value: z.string(),
	code: z.union([
		z.literal('AED'),
		z.literal('AUD'),
		z.literal('AZN'),
		z.literal('BGN'),
		z.literal('BHD'),
		z.literal('BSD'),
		z.literal('CAD'),
		z.literal('CHF'),
		z.literal('CRC'),
		z.literal('CZK'),
		z.literal('EGP'),
		z.literal('EUR'),
		z.literal('GBP'),
		z.literal('HNL'),
		z.literal('INR'),
		z.literal('JOD'),
		z.literal('KHR'),
		z.literal('KRW'),
		z.literal('LKR'),
		z.literal('MAD'),
		z.literal('MXN'),
		z.literal('MYR'),
		z.literal('NOK'),
		z.literal('OMR'),
		z.literal('PAB'),
		z.literal('SAR'),
		z.literal('TWD'),
		z.literal('USD'),
	]),
	cents: z.coerce.number(),
	displayValue: z.string(),
});

export type AddonPreviewResponse = z.infer<typeof AddonPreviewResponse>;
export const AddonPreviewResponse = z.object({
	items: z.array(LineItem),
	amountDueToday: Currency,
	renewalMessage: z.string(),
	promoCode: z.union([z.string(), z.null()]),
});

export type AddAddonPurchaseRequestBodyDto = z.infer<typeof AddAddonPurchaseRequestBodyDto>;
export const AddAddonPurchaseRequestBodyDto = z.object({
	subscriptionId: z.string(),
	troonCardSubscriptionAddonId: z.string(),
	promoCode: z.union([z.string(), z.undefined()]).optional(),
	paymentMethodId: z.union([z.string(), z.undefined()]).optional(),
});

export type Timestamp = z.infer<typeof Timestamp>;
export const Timestamp = z.object({
	epoch: z.coerce.number(),
	tz: z.string(),
});

export type AddonPurchaseResponse = z.infer<typeof AddonPurchaseResponse>;
export const AddonPurchaseResponse = z.object({
	stripeSubscription: z.object({
		nextInvoiceDate: z.union([Timestamp, z.null()]),
		discountAmount: Currency,
		subtotalAmount: Currency,
		taxAmount: Currency,
		totalAmount: Currency,
		endDate: z.union([Timestamp, z.null()]),
	}),
});

export type TroonCardSubscriptionAddonType = z.infer<typeof TroonCardSubscriptionAddonType>;
export const TroonCardSubscriptionAddonType = z.literal('UNLIMITED_GUEST_PASSES');

export type AddonProductPrice = z.infer<typeof AddonProductPrice>;
export const AddonProductPrice = z.object({
	amount: Currency,
	interval: z.union([z.union([z.literal('day'), z.literal('week'), z.literal('month'), z.literal('year')]), z.null()]),
});

export type AddonProductResponse = z.infer<typeof AddonProductResponse>;
export const AddonProductResponse = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string(),
	active: z.coerce.boolean(),
	type: TroonCardSubscriptionAddonType,
	stripeProductId: z.string(),
	stripePriceId: z.string(),
	price: AddonProductPrice,
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type GetAddonProductsResponse = z.infer<typeof GetAddonProductsResponse>;
export const GetAddonProductsResponse = z.object({
	products: z.array(AddonProductResponse),
});

export type CalendarDayTime = z.infer<typeof CalendarDayTime>;
export const CalendarDayTime = z.object({
	year: z.coerce.number(),
	month: z.coerce.number(),
	day: z.coerce.number(),
	hour: z.coerce.number(),
	minute: z.coerce.number(),
	second: z.union([z.union([z.coerce.number(), z.null()]), z.undefined()]).optional(),
});

export type TeeTimePlayers = z.infer<typeof TeeTimePlayers>;
export const TeeTimePlayers = z.object({
	min: z.coerce.number(),
	max: z.coerce.number(),
});

export type TeeTimePrice = z.infer<typeof TeeTimePrice>;
export const TeeTimePrice = z.object({
	min: Currency,
	max: Currency,
});

export type RoundHolesOption = z.infer<typeof RoundHolesOption>;
export const RoundHolesOption = z.union([z.literal('NINE'), z.literal('EIGHTEEN'), z.literal('NINE_OR_EIGHTEEN')]);

export type TeeTimeRateType = z.infer<typeof TeeTimeRateType>;
export const TeeTimeRateType = z.union([
	z.literal('PUBLIC'),
	z.literal('PUBLIC_EXTRA'),
	z.literal('TROON_ACCESS'),
	z.literal('TROON_ACCESS_PLUS'),
	z.literal('TROON_CARD_TWOSOME'),
	z.literal('TROON_CARD_FOURSOME'),
	z.literal('LOCAL_CARD'),
]);

export type TeeTimeRate = z.infer<typeof TeeTimeRate>;
export const TeeTimeRate = z.object({
	id: z.string(),
	name: z.string(),
	description: z.union([z.string(), z.null()]),
	dayTime: CalendarDayTime,
	players: z.array(z.coerce.number()),
	price: Currency,
	holesOption: RoundHolesOption,
	cartIncluded: z.coerce.boolean(),
	practiceBallsIncluded: z.coerce.boolean(),
	isPrePaid: z.coerce.boolean(),
	isAvailableToUser: z.coerce.boolean(),
	rateType: TeeTimeRateType,
	isHotDeal: z.coerce.boolean(),
	discount: z.union([
		z.object({
			difference: Currency,
			discountPercent: z.coerce.number(),
			originalPrice: Currency,
		}),
		z.null(),
	]),
});

export type PaymentProcessor = z.infer<typeof PaymentProcessor>;
export const PaymentProcessor = z.union([z.literal('GOLFNOW'), z.literal('STRIPE')]);

export type TeeTimeCourse = z.infer<typeof TeeTimeCourse>;
export const TeeTimeCourse = z.object({
	id: z.string(),
	slug: z.string(),
	name: z.string(),
	paymentProcessor: z.union([PaymentProcessor, z.null()]),
});

export type TeeTimeFacility = z.infer<typeof TeeTimeFacility>;
export const TeeTimeFacility = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	logoUrl: z.union([z.string(), z.null()]),
	heroUrl: z.union([z.string(), z.null()]),
	supportsAccessPoints: z.coerce.boolean(),
	supportsAccessRewards: z.coerce.boolean(),
	supportsSplitRedemption: z.coerce.boolean(),
	supportsPointsRedemption: z.coerce.boolean(),
});

export type TeeTime = z.infer<typeof TeeTime>;
export const TeeTime = z.object({
	id: z.string(),
	dayTime: CalendarDayTime,
	players: TeeTimePlayers,
	price: TeeTimePrice,
	holesOption: RoundHolesOption,
	cartIncluded: z.coerce.boolean(),
	practiceBallsIncluded: z.coerce.boolean(),
	rates: z.array(TeeTimeRate),
	course: TeeTimeCourse,
	facility: TeeTimeFacility,
	hasAccessPlusDeal: z.coerce.boolean(),
	includesHotDeal: z.coerce.boolean(),
});

export type TeeTimesResponse = z.infer<typeof TeeTimesResponse>;
export const TeeTimesResponse = z.object({
	teeTimes: z.array(TeeTime),
});

export type TeeTimeRateV1 = z.infer<typeof TeeTimeRateV1>;
export const TeeTimeRateV1 = z.object({
	id: z.string(),
	name: z.string(),
	description: z.union([z.string(), z.null()]),
	dayTime: CalendarDayTime,
	players: z.array(z.coerce.number()),
	price: z.object({
		dollars: Currency,
		points: z.union([z.coerce.number(), z.null()]),
		split: z.union([
			z.object({
				dollars: Currency,
				points: z.union([z.coerce.number(), z.null()]),
			}),
			z.null(),
		]),
	}),
	holesOption: RoundHolesOption,
	cartIncluded: z.coerce.boolean(),
	practiceBallsIncluded: z.coerce.boolean(),
	isPrePaid: z.coerce.boolean(),
	isAvailableToUser: z.coerce.boolean(),
	rateType: TeeTimeRateType,
	isHotDeal: z.coerce.boolean(),
	discount: z.union([
		z.object({
			difference: Currency,
			discountPercent: z.coerce.number(),
			originalPrice: Currency,
		}),
		z.null(),
	]),
	accessRewards: z.union([
		z.object({
			eligibleForRedemption: z.coerce.boolean(),
			accessPointsDiscount: z.union([
				z.object({
					difference: z.coerce.number(),
					discountPercent: z.coerce.number(),
					originalPrice: z.coerce.number(),
				}),
				z.null(),
			]),
		}),
		z.null(),
	]),
	earnsAccessRewards: z.coerce.boolean(),
});

export type TeeTimePoints = z.infer<typeof TeeTimePoints>;
export const TeeTimePoints = z.object({
	min: z.coerce.number(),
	max: z.coerce.number(),
});

export type TeeTimeV1 = z.infer<typeof TeeTimeV1>;
export const TeeTimeV1 = z.object({
	id: z.string(),
	dayTime: CalendarDayTime,
	players: TeeTimePlayers,
	price: TeeTimePrice,
	holesOption: RoundHolesOption,
	cartIncluded: z.coerce.boolean(),
	practiceBallsIncluded: z.coerce.boolean(),
	rates: z.array(TeeTimeRateV1),
	course: TeeTimeCourse,
	facility: TeeTimeFacility,
	hasAccessPlusDeal: z.coerce.boolean(),
	includesHotDeal: z.coerce.boolean(),
	accessPoints: z.union([TeeTimePoints, z.null()]),
	accessRewardsRedemptionRateId: z.union([z.string(), z.undefined()]).optional(),
	displayRate: z.union([TeeTimeRateV1, z.null()]),
});

export type TeeTimesResponseDtoV1 = z.infer<typeof TeeTimesResponseDtoV1>;
export const TeeTimesResponseDtoV1 = z.object({
	teeTimes: z.array(TeeTimeV1),
});

export type ReceiptItemType = z.infer<typeof ReceiptItemType>;
export const ReceiptItemType = z.union([z.literal('CHARGE'), z.literal('DISCOUNT')]);

export type ReceiptItem = z.infer<typeof ReceiptItem>;
export const ReceiptItem = z.object({
	label: z.string(),
	amount: Currency,
	points: z.union([z.coerce.number(), z.null()]),
	displayAmount: z.string(),
	itemType: ReceiptItemType,
});

export type Reimbursement = z.infer<typeof Reimbursement>;
export const Reimbursement = z.object({
	amount: z.union([Currency, z.null()]),
	disclaimer: z.string(),
});

export type ReceiptTotal = z.infer<typeof ReceiptTotal>;
export const ReceiptTotal = z.object({
	amount: Currency,
	label: z.string(),
});

export type Receipt = z.infer<typeof Receipt>;
export const Receipt = z.object({
	savings: Currency,
	items: z.array(ReceiptItem),
	reimbursement: z.union([Reimbursement, z.null()]),
	totals: z.array(ReceiptTotal),
	disclaimer: z.union([z.string(), z.null()]),
});

export type RewardsInfo = z.infer<typeof RewardsInfo>;
export const RewardsInfo = z.object({
	rewardPointsUsed: z.coerce.number(),
	rewardPointsEarned: z.coerce.number(),
	accessPointsEarned: z.coerce.number(),
});

export type TeeTimePaymentInfo = z.infer<typeof TeeTimePaymentInfo>;
export const TeeTimePaymentInfo = z.object({
	receipt: Receipt,
	rewardsInfo: RewardsInfo,
	requiresCreditCard: z.coerce.boolean(),
	paymentProcessor: z.union([z.literal('GOLFNOW'), z.literal('STRIPE')]),
});

export type TeeTimeCancellationInfo = z.infer<typeof TeeTimeCancellationInfo>;
export const TeeTimeCancellationInfo = z.object({
	cancellationThresholdInHours: z.coerce.number(),
	cancellationFee: Currency,
	isCancellable: z.coerce.boolean(),
	displayMessage: z.string(),
});

export type TeeTimeReservationInfo = z.infer<typeof TeeTimeReservationInfo>;
export const TeeTimeReservationInfo = z.object({
	reservationInfo: z.union([
		z.object({
			reserveId: z.string(),
			rates: z.array(TeeTimeRate),
			selectedRate: TeeTimeRate,
			players: z.coerce.number(),
			holes: z.union([z.literal('NINE'), z.literal('EIGHTEEN'), z.literal('NINE_OR_EIGHTEEN')]),
			paymentInfo: TeeTimePaymentInfo,
			cancellationInfo: TeeTimeCancellationInfo,
			courseNotes: z.union([z.string(), z.null()]),
		}),
		z.null(),
	]),
	dayTime: CalendarDayTime,
	facility: TeeTimeFacility,
	course: TeeTimeCourse,
});

export type TeeTimeReservationInfoDtoV1 = z.infer<typeof TeeTimeReservationInfoDtoV1>;
export const TeeTimeReservationInfoDtoV1 = z.object({
	reservationInfo: z.union([
		z.object({
			reserveId: z.string(),
			rates: z.array(TeeTimeRateV1),
			selectedRate: TeeTimeRateV1,
			players: z.coerce.number(),
			holes: z.union([z.literal('NINE'), z.literal('EIGHTEEN'), z.literal('NINE_OR_EIGHTEEN')]),
			paymentInfo: TeeTimePaymentInfo,
			cancellationInfo: TeeTimeCancellationInfo,
			courseNotes: z.union([z.string(), z.null()]),
			teeTime: TeeTimeV1,
			accessRewardsDetails: z.union([
				z.object({
					redemptionOptions: z.union([
						z.array(
							z.object({
								name: z.string(),
								price: z.string(),
								publicPrice: z.string(),
								rateType: z.string(),
								description: z.union([z.string(), z.null()]),
								disabled: z.union([z.coerce.boolean(), z.null()]),
								redeemForAll: z.coerce.boolean(),
								payMode: z.union([z.literal('points'), z.literal('split')]),
							}),
						),
						z.null(),
					]),
					remainingPoints: z.union([z.coerce.number(), z.null()]),
				}),
				z.null(),
			]),
			rateSelectNotices: z.array(
				z.object({
					id: z.string(),
					appearance: z.union([z.literal('warning'), z.literal('info')]),
					message: z.string(),
					action: z
						.union([
							z.object({
								type: z.literal('open_access_opt_in_dialog'),
								label: z.string(),
							}),
							z.undefined(),
						])
						.optional(),
				}),
			),
		}),
		z.null(),
	]),
	guestPassInfo: z.object({
		canApplyAtBooking: z.coerce.boolean(),
		available: z.coerce.number(),
		hasUnlimitedGuestPass: z.coerce.boolean(),
		isAccessGuestRateProgramCourse: z.coerce.boolean(),
	}),
	dayTime: CalendarDayTime,
	facility: TeeTimeFacility,
	course: TeeTimeCourse,
});

export type PaymentMethod = z.infer<typeof PaymentMethod>;
export const PaymentMethod = z.object({
	id: z.string(),
	type: z.string(),
	default: z.coerce.boolean(),
	card: z.object({
		brand: z.union([
			z.literal('VISA'),
			z.literal('MASTERCARD'),
			z.literal('AMEX'),
			z.literal('DISCOVER'),
			z.literal('OTHER'),
		]),
		last4: z.string(),
		exp_month: z.coerce.number(),
		exp_year: z.coerce.number(),
	}),
});

export type StripePaymentMethods = z.infer<typeof StripePaymentMethods>;
export const StripePaymentMethods = z.object({
	payment_methods: z.array(PaymentMethod),
	client_secret: z.string(),
});

export type AddPaymentMethodBody = z.infer<typeof AddPaymentMethodBody>;
export const AddPaymentMethodBody = z.object({
	payment_method_id: z.string(),
});

export type AddPaymentMethodResponse = z.infer<typeof AddPaymentMethodResponse>;
export const AddPaymentMethodResponse = z.object({
	success: z.coerce.boolean(),
	payment_method_id: z.string(),
});

export type TroonAccessReservationUpsell = z.infer<typeof TroonAccessReservationUpsell>;
export const TroonAccessReservationUpsell = z.object({
	savings: Currency,
	displayMessage: z.string(),
});

export type TroonAccessReservationUpsellResponse = z.infer<typeof TroonAccessReservationUpsellResponse>;
export const TroonAccessReservationUpsellResponse = z.object({
	upsell: z.union([TroonAccessReservationUpsell, z.null()]).optional(),
});

export type TroonAccessTotalSavings = z.infer<typeof TroonAccessTotalSavings>;
export const TroonAccessTotalSavings = z.object({
	avgAccessSavingsPerUser: Currency,
	avgAccessPlusSavingsPerUser: Currency,
	avgAccessSavingsPerRound: Currency,
	avgAccessPlusSavingsPerRound: Currency,
});

export type TroonAccessTotalSavingsResponse = z.infer<typeof TroonAccessTotalSavingsResponse>;
export const TroonAccessTotalSavingsResponse = z.object({
	savings: z.union([TroonAccessTotalSavings, z.null()]).optional(),
});

export type ReservationState = z.infer<typeof ReservationState>;
export const ReservationState = z.union([
	z.literal('ACTIVE'),
	z.literal('PENDING'),
	z.literal('CANCELLED'),
	z.literal('COMPLETED'),
	z.literal('FAILED'),
]);

export type CalendarEvent = z.infer<typeof CalendarEvent>;
export const CalendarEvent = z.object({
	google: z.string(),
	outlook: z.string(),
	office365: z.string(),
	yahoo: z.string(),
	ics: z.string(),
});

export type FacilityDisplayConfig = z.infer<typeof FacilityDisplayConfig>;
export const FacilityDisplayConfig = z.union([
	z.literal('FEATURED_ACCESS_OFFERS'),
	z.literal('INLINE_ACCESS_OFFERS'),
	z.literal('INLINE_ACCESS_UPSELL'),
	z.literal('ACCESS_FILTER'),
	z.literal('RESERVE_ACCESS_RATE'),
	z.literal('GOLFNOW_OFFER_FILTER'),
	z.literal('GOLFNOW_OFFER_RATES'),
]);

export type TroonFacilityType = z.infer<typeof TroonFacilityType>;
export const TroonFacilityType = z.union([
	z.literal('DAILY_FEE_RESORT'),
	z.literal('PRIVATE'),
	z.literal('SEMI_PRIVATE'),
	z.literal('HOSPITALITY'),
	z.literal('RESIDENTIAL'),
]);

export type TroonFacilityBrand = z.infer<typeof TroonFacilityBrand>;
export const TroonFacilityBrand = z.union([
	z.literal('TROON_PRIVE'),
	z.literal('TROON_PRIVE_INTL'),
	z.literal('TROON_GOLF'),
	z.literal('TROON_GOLF_INTL'),
	z.literal('TROON_PARTNER'),
	z.literal('OB_SPORTS'),
	z.literal('INDIGO_SPORTS'),
	z.literal('ICON'),
	z.literal('NONE'),
]);

export type Address = z.infer<typeof Address>;
export const Address = z.object({
	street: z.union([z.string(), z.null()]),
	street2: z.union([z.string(), z.null()]),
	city: z.union([z.string(), z.null()]),
	state: z.union([z.string(), z.null()]),
	country: z.union([z.string(), z.null()]),
	postalCode: z.union([z.string(), z.null()]),
});

export type Course = z.infer<typeof Course>;
export const Course = z.object({
	id: z.string(),
	name: z.string(),
	facilityName: z.union([z.string(), z.null()]),
	slug: z.string(),
	holeCount: z.union([z.coerce.number(), z.null()]),
	isBookable: z.coerce.boolean(),
	canCancelTeeTimes: z.coerce.boolean(),
	requireCCAtBooking: z.coerce.boolean(),
	checkInEnabled: z.coerce.boolean(),
	supportsTroonRewards: z.coerce.boolean(),
	supportsTroonAccess: z.coerce.boolean(),
	supportsTeeTimeAlerts: z.coerce.boolean(),
	bookingWindowInDays: z.coerce.number(),
	cancellationHoursThreshold: z.union([z.coerce.number(), z.null()]),
});

export type FacilityClosure = z.infer<typeof FacilityClosure>;
export const FacilityClosure = z.object({
	id: z.string(),
	reason: z.union([z.string(), z.null()]),
	startTime: Timestamp,
	endTime: Timestamp,
	courseIds: z.array(z.string()),
});

export type FacilityNote = z.infer<typeof FacilityNote>;
export const FacilityNote = z.object({
	id: z.string(),
	note: z.string(),
	startDate: Timestamp,
	endDate: Timestamp,
});

export type Facility = z.infer<typeof Facility>;
export const Facility = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	logo: z.union([z.string(), z.null()]),
	hero: z.union([z.string(), z.null()]),
	gallery: z.array(z.string()),
	timezone: z.string(),
	latitude: z.union([z.coerce.number(), z.null()]),
	longitude: z.union([z.coerce.number(), z.null()]),
	isBookable: z.coerce.boolean(),
	isFavorite: z.coerce.boolean(),
	supportsTroonAccess: z.coerce.boolean(),
	supportsTroonRewards: z.coerce.boolean(),
	supportsTroonAdvantage: z.coerce.boolean(),
	supportsAccessRewards: z.coerce.boolean(),
	supportsAccessPoints: z.coerce.boolean(),
	supportsPointsRedemption: z.coerce.boolean(),
	supportsSplitRedemption: z.coerce.boolean(),
	supportsTroonAccessCourseSiteUpsell: z.coerce.boolean(),
	displayConfig: z.array(FacilityDisplayConfig),
	supportsTroonAccessPrive: z.coerce.boolean(),
	supportsHotDeals: z.coerce.boolean(),
	isFeatured: z.coerce.boolean(),
	type: TroonFacilityType,
	brand: TroonFacilityBrand,
	address: z.union([Address, z.null()]),
	description: z.union([z.string(), z.null()]),
	phone: z.union([z.string(), z.null()]),
	website: z.union([z.string(), z.null()]),
	amenities: z.object({
		swimming: z.coerce.boolean(),
		fitness: z.coerce.boolean(),
		tennis: z.coerce.boolean(),
		spa: z.coerce.boolean(),
		dining: z.coerce.boolean(),
		caddieMaster: z.coerce.boolean(),
	}),
	courses: z.array(Course),
	closures: z.array(FacilityClosure),
	notes: z.array(FacilityNote),
});

export type User = z.infer<typeof User>;
export const User = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	troonAccessProductType: z.union([TroonCardSubscriptionProductType, z.null()]),
});

export type ReservationUserState = z.infer<typeof ReservationUserState>;
export const ReservationUserState = z.union([
	z.literal('EMPTY'),
	z.literal('INVITED'),
	z.literal('ACCEPTED'),
	z.literal('CANCELLED'),
]);

export type GuestPassStatus = z.infer<typeof GuestPassStatus>;
export const GuestPassStatus = z.union([z.literal('AVAILABLE'), z.literal('ACCEPTED'), z.literal('CANCELLED')]);

export type ReservationGuestPass = z.infer<typeof ReservationGuestPass>;
export const ReservationGuestPass = z.object({
	id: z.string(),
	status: GuestPassStatus,
	isLocked: z.coerce.boolean(),
	discount: z.union([Currency, z.null()]),
});

export type ReservationUser = z.infer<typeof ReservationUser>;
export const ReservationUser = z.object({
	id: z.string(),
	state: ReservationUserState,
	isCheckedIn: z.coerce.boolean(),
	fee: Currency,
	rateType: TeeTimeRateType,
	user: z.union([User, z.null()]),
	guestPass: z.union([ReservationGuestPass, z.null()]),
	canRedeemPoints: z.coerce.boolean(),
});

export type Reservation = z.infer<typeof Reservation>;
export const Reservation = z.object({
	id: z.string(),
	teeTimeId: z.string(),
	state: ReservationState,
	playerCount: z.coerce.number(),
	holeCount: z.coerce.number(),
	includesCart: z.coerce.boolean(),
	teeTime: Timestamp,
	cancelBy: z.union([Timestamp, z.null()]),
	cancelFee: z.union([Currency, z.null()]),
	checkInEnabled: z.coerce.boolean(),
	timezone: z.string(),
	publicFee: z.union([Currency, z.null()]),
	troonAccessFee: z.union([Currency, z.null()]),
	troonAccessPlusFee: z.union([Currency, z.null()]),
	allowsGuestPasses: z.coerce.boolean(),
	guestPassDiscount: z.union([Currency, z.null()]),
	bookingRateType: z.union([TeeTimeRateType, z.null()]),
	autoApplyGuestPasses: z.coerce.boolean(),
	isPrePaid: z.coerce.boolean(),
	isHotDeal: z.coerce.boolean(),
	shareInviteUrl: z.string(),
	calendarEvent: CalendarEvent,
	facility: Facility,
	course: Course,
	owner: User,
	users: z.array(ReservationUser),
});

export type TroonAccessReservationSavings = z.infer<typeof TroonAccessReservationSavings>;
export const TroonAccessReservationSavings = z.object({
	accessDiscount: Currency,
	accessPlusDiscount: Currency,
	reservation: Reservation,
});

export type TroonAccessReservationSavingsList = z.infer<typeof TroonAccessReservationSavingsList>;
export const TroonAccessReservationSavingsList = z.object({
	reservations: z.array(TroonAccessReservationSavings),
});

export type AccessPremiumTeeTimeDeal = z.infer<typeof AccessPremiumTeeTimeDeal>;
export const AccessPremiumTeeTimeDeal = z.object({
	teeTimeId: z.string(),
	facilityId: z.string(),
	courseId: z.string(),
	publicRate: Currency,
	accessPlusRate: Currency,
	discount: Currency,
	dayTime: CalendarDayTime,
	minPlayers: z.coerce.number(),
	maxPlayers: z.coerce.number(),
});

export type AccessPremiumTeeTimeDealByCourse = z.infer<typeof AccessPremiumTeeTimeDealByCourse>;
export const AccessPremiumTeeTimeDealByCourse = z.object({
	course: Course,
	facility: Facility,
	deals: z.array(AccessPremiumTeeTimeDeal),
});

export type AccessPremiumTeeTimeDealsResponse = z.infer<typeof AccessPremiumTeeTimeDealsResponse>;
export const AccessPremiumTeeTimeDealsResponse = z.object({
	dealsByCourse: z.array(AccessPremiumTeeTimeDealByCourse),
});

export type CalendarDay = z.infer<typeof CalendarDay>;
export const CalendarDay = z.object({
	year: z.coerce.number(),
	month: z.coerce.number(),
	day: z.coerce.number(),
});

export type TroonAccessSubscription = z.infer<typeof TroonAccessSubscription>;
export const TroonAccessSubscription = z.object({
	id: z.string(),
	status: z.union([
		z.literal('PENDING'),
		z.literal('ACTIVE'),
		z.literal('CANCELLED'),
		z.literal('EXPIRED'),
		z.literal('REFUNDED'),
	]),
	subscriptionName: z.string(),
	startDate: z.union([z.union([CalendarDay, z.null()]), z.undefined()]).optional(),
	endDate: z.union([z.union([CalendarDay, z.null()]), z.undefined()]).optional(),
	totalAmount: Currency,
	subtotalAmount: Currency,
	discountAmount: Currency,
	taxAmount: Currency,
	nextInvoiceAmount: z.union([z.union([Currency, z.null()]), z.undefined()]).optional(),
	nextInvoiceDate: z.union([z.union([CalendarDay, z.null()]), z.undefined()]).optional(),
	autoRenews: z.coerce.boolean(),
	productType: z.union([z.literal('TROON_ACCESS'), z.literal('TROON_ACCESS_PLUS'), z.literal('TROON_ACCESS_ELITE')]),
	manageSubscriptionUrl: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	updateSubscriptionUrl: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	unlimitedGuestPass: z.coerce.boolean(),
	productAddOns: z.array(z.literal('UNLIMITED_GUEST_PASSES')),
});

export type TroonAccessSubscriptionProduct = z.infer<typeof TroonAccessSubscriptionProduct>;
export const TroonAccessSubscriptionProduct = z.object({
	id: z.string(),
	active: z.coerce.boolean(),
	description: z.string(),
	subscriptionName: z.string(),
	amount: Currency,
	monthlyAmount: Currency,
	totalAmount: Currency,
	monthlyTotalAmount: Currency,
	subtotalAmount: Currency,
	monthlySubtotalAmount: Currency,
	discountAmount: Currency,
	monthlyDiscountAmount: Currency,
	type: z.union([z.literal('TROON_ACCESS'), z.literal('TROON_ACCESS_PLUS'), z.literal('TROON_ACCESS_ELITE')]),
	valueProps: z.array(z.string()),
	additionalValueProps: z.union([z.union([z.array(z.string()), z.null()]), z.undefined()]).optional(),
	isPopular: z.coerce.boolean(),
	autoRenew: z.coerce.boolean(),
	disclosure: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
});

export type TroonAccessSubscriptionProductsResponse = z.infer<typeof TroonAccessSubscriptionProductsResponse>;
export const TroonAccessSubscriptionProductsResponse = z.object({
	products: z.array(TroonAccessSubscriptionProduct),
});

export type TroonAccessAssociateInfo = z.infer<typeof TroonAccessAssociateInfo>;
export const TroonAccessAssociateInfo = z.object({
	associateName: z.string(),
	rewardsId: z.string(),
	facilityId: z.string(),
	operatorId: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	publicRate: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
});

export type SetupTroonAccessSubscriptionBody = z.infer<typeof SetupTroonAccessSubscriptionBody>;
export const SetupTroonAccessSubscriptionBody = z.object({
	troonCardSubscriptionProductId: z.string(),
	promoCode: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	source: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	troonCardAssociateInfo: z.union([z.union([TroonAccessAssociateInfo, z.null()]), z.undefined()]).optional(),
});

export type TroonAccessSubscriptionIntent = z.infer<typeof TroonAccessSubscriptionIntent>;
export const TroonAccessSubscriptionIntent = z.object({
	stripeClientSecret: z.string(),
	stripeClientSecretIntentType: z.union([z.literal('PAYMENT'), z.literal('SETUP')]),
	stripeCustomerId: z.string(),
	stripeCustomerEphemeralKey: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	subscriptionName: z.string(),
	troonCardSubscriptionId: z.string(),
	totalAmount: Currency,
	subtotalAmount: Currency,
	discountAmount: Currency,
	taxAmount: Currency,
	promoCode: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	disclosure: z.string(),
});

export type ConfirmTroonAccessSubscriptionBody = z.infer<typeof ConfirmTroonAccessSubscriptionBody>;
export const ConfirmTroonAccessSubscriptionBody = z.object({
	troonCardSubscriptionId: z.string(),
});

export type PreviewAccessPremiumMembershipUpgradeBody = z.infer<typeof PreviewAccessPremiumMembershipUpgradeBody>;
export const PreviewAccessPremiumMembershipUpgradeBody = z.object({
	targetProductType: z.union([
		z.literal('TROON_ACCESS'),
		z.literal('TROON_ACCESS_PLUS'),
		z.literal('TROON_ACCESS_ELITE'),
	]),
	promoCode: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
});

export type AccessPremiumMembershipUpgradeLineItem = z.infer<typeof AccessPremiumMembershipUpgradeLineItem>;
export const AccessPremiumMembershipUpgradeLineItem = z.object({
	name: z.union([z.string(), z.null()]),
	price: z.coerce.number(),
	priceDisplay: z.string(),
	description: z.union([z.string(), z.null()]),
});

export type AccessPremiumMembershipUpgradePreviewResponse = z.infer<
	typeof AccessPremiumMembershipUpgradePreviewResponse
>;
export const AccessPremiumMembershipUpgradePreviewResponse = z.object({
	items: z.array(AccessPremiumMembershipUpgradeLineItem),
	amountDueToday: Currency,
	renewalMessage: z.string(),
	promoCode: z.union([z.string(), z.null()]),
	fromProductType: z.union([
		z.literal('TROON_ACCESS'),
		z.literal('TROON_ACCESS_PLUS'),
		z.literal('TROON_ACCESS_ELITE'),
	]),
	toProductType: z.union([z.literal('TROON_ACCESS'), z.literal('TROON_ACCESS_PLUS'), z.literal('TROON_ACCESS_ELITE')]),
});

export type ConfirmAccessPremiumMembershipUpgradeBody = z.infer<typeof ConfirmAccessPremiumMembershipUpgradeBody>;
export const ConfirmAccessPremiumMembershipUpgradeBody = z.object({
	targetProductType: z.union([
		z.literal('TROON_ACCESS'),
		z.literal('TROON_ACCESS_PLUS'),
		z.literal('TROON_ACCESS_ELITE'),
	]),
	promoCode: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	paymentMethodId: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
});

export type AccessPremiumMembershipUpgradeConfirmResponse = z.infer<
	typeof AccessPremiumMembershipUpgradeConfirmResponse
>;
export const AccessPremiumMembershipUpgradeConfirmResponse = z.object({
	stripeSubscription: z.object({
		nextInvoiceDate: z.union([Timestamp, z.null()]),
		discountAmount: Currency,
		subtotalAmount: Currency,
		taxAmount: Currency,
		totalAmount: Currency,
		endDate: z.union([Timestamp, z.null()]),
	}),
	fromProductType: z.union([
		z.literal('TROON_ACCESS'),
		z.literal('TROON_ACCESS_PLUS'),
		z.literal('TROON_ACCESS_ELITE'),
	]),
	toProductType: z.union([z.literal('TROON_ACCESS'), z.literal('TROON_ACCESS_PLUS'), z.literal('TROON_ACCESS_ELITE')]),
});

export type VisaProgram = z.infer<typeof VisaProgram>;
export const VisaProgram = z.union([
	z.literal('VISA_INF'),
	z.literal('VISA_SIG'),
	z.literal('VISA_CAD_INF'),
	z.literal('VISA_CAD_INF_PRIV'),
]);

export type VisaStatus = z.infer<typeof VisaStatus>;
export const VisaStatus = z.object({
	programType: VisaProgram,
	privateEligible: z.coerce.boolean(),
	expiresAt: z.string(),
	canUpgrade: z.coerce.boolean(),
});

export type VisaStatusResponse = z.infer<typeof VisaStatusResponse>;
export const VisaStatusResponse = z.object({
	data: z.union([VisaStatus, z.null()]),
});

export type VisaVerificationRequestState = z.infer<typeof VisaVerificationRequestState>;
export const VisaVerificationRequestState = z.union([z.literal('PENDING'), z.literal('VERIFIED'), z.literal('FAILED')]);

export type VisaTokenResult = z.infer<typeof VisaTokenResult>;
export const VisaTokenResult = z.object({
	state: VisaVerificationRequestState,
	visaStatus: z.union([z.union([VisaStatus, z.null()]), z.undefined()]).optional(),
	originalRewardsLevel: TroonRewardsLevel,
	newRewardsLevel: z.union([z.union([TroonRewardsLevel, z.null()]), z.undefined()]).optional(),
});

export type VisaToken = z.infer<typeof VisaToken>;
export const VisaToken = z.object({
	token: z.string(),
});

export type PriveTeeTimeRequestType = z.infer<typeof PriveTeeTimeRequestType>;
export const PriveTeeTimeRequestType = z.union([z.literal('PRIVE'), z.literal('VISA'), z.literal('TROON_ACCESS')]);

export type PriveTeeTimeRequest = z.infer<typeof PriveTeeTimeRequest>;
export const PriveTeeTimeRequest = z.object({
	id: z.string(),
	guestCount: z.coerce.number(),
	memberCount: z.coerce.number(),
	dayTime: Timestamp,
	type: PriveTeeTimeRequestType,
	facility: z.object({
		id: z.string(),
		name: z.string(),
	}),
});

export type PriveRequestBody = z.infer<typeof PriveRequestBody>;
export const PriveRequestBody = z.object({
	facilityId: z.string(),
	dayTime: Timestamp,
	guests: z.coerce.number(),
	comments: z.union([z.string(), z.undefined()]).optional(),
	priveRequestType: z.union([PriveTeeTimeRequestType, z.undefined()]).optional(),
});

export type UserExistsResponse = z.infer<typeof UserExistsResponse>;
export const UserExistsResponse = z.object({
	status: z.union([z.literal('NO_ACCOUNT'), z.literal('EXISTS_AND_FIRST_LOGIN'), z.literal('EXISTS')]),
});

export type TroonRewardsAccount = z.infer<typeof TroonRewardsAccount>;
export const TroonRewardsAccount = z.object({
	rewardsNumber: z.string(),
	level: z.union([z.literal('MEMBER'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
	points: z.object({
		available: z.coerce.number(),
		yearToDate: z.coerce.number(),
	}),
	currentLevel: z.object({
		level: z.union([z.literal('MEMBER'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
		requiredPoints: z.coerce.number(),
		discount: z.union([z.coerce.number(), z.null()]),
	}),
	nextLevel: z.union([
		z.object({
			level: z.union([z.literal('MEMBER'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
			requiredPoints: z.coerce.number(),
			discount: z.coerce.number(),
		}),
		z.null(),
	]),
});

export type TroonAccessAccountSchema = z.infer<typeof TroonAccessAccountSchema>;
export const TroonAccessAccountSchema = z.object({
	guestPassesAvailable: z.coerce.number(),
	productAddOns: z.array(z.literal('UNLIMITED_GUEST_PASSES')),
	productType: z.union([
		z.union([z.literal('TROON_ACCESS'), z.literal('TROON_ACCESS_PLUS'), z.literal('TROON_ACCESS_ELITE')]),
		z.null(),
	]),
	renewalDate: z.union([Timestamp, z.null()]),
	cancellationDate: z.union([Timestamp, z.null()]),
});

export type TroonAccessSavingsSchema = z.infer<typeof TroonAccessSavingsSchema>;
export const TroonAccessSavingsSchema = z.object({
	personal: z.union([Currency, z.null()]),
	guest: z.union([Currency, z.null()]),
	rounds: z.coerce.number(),
});

export type TroonProgramAccountSchema = z.infer<typeof TroonProgramAccountSchema>;
export const TroonProgramAccountSchema = z.object({
	id: z.string(),
	name: z.string(),
	type: z.union([
		z.literal('BAYCLUB'),
		z.literal('VILLAGE_GAINEY'),
		z.literal('VILLAGE_CAMELBACK'),
		z.literal('VILLAGE_DC_RANCH'),
		z.literal('VILLAGE_OCOTILLO'),
		z.literal('VISA_SIGNATURE'),
		z.literal('VISA_INFINITE'),
		z.literal('VISA_CA_INFINITE'),
		z.literal('VISA_CA_INFINITE_PRIVILEGE'),
	]),
	expiresAt: z.union([Timestamp, z.null()]),
});

export type UserAccount = z.infer<typeof UserAccount>;
export const UserAccount = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	rewards: TroonRewardsAccount,
	access: z.union([TroonAccessAccountSchema, z.null()]),
	savings: TroonAccessSavingsSchema,
	programs: z.array(TroonProgramAccountSchema),
	memberSince: Timestamp,
	currentSubscriptionStartDate: z.union([Timestamp, z.null()]),
});

export type AccessPointsAccount = z.infer<typeof AccessPointsAccount>;
export const AccessPointsAccount = z.object({
	availablePoints: z.coerce.number(),
	pendingPoints: z.coerce.number(),
	lifetimeEarnedPoints: z.coerce.number(),
	lifetimeRedeemedPoints: z.coerce.number(),
	currentTierPoints: z.coerce.number(),
	currentTier: z.union([
		z.object({
			tier: z.union([z.literal('BASE'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
			minimumPoints: z.coerce.number(),
			pointsMultiplier: z.coerce.number(),
			guestPasses: z.coerce.number(),
		}),
		z.null(),
	]),
	nextTier: z.union([
		z.object({
			tier: z.union([z.literal('BASE'), z.literal('SILVER'), z.literal('GOLD'), z.literal('PLATINUM')]),
			minimumPoints: z.coerce.number(),
			pointsMultiplier: z.coerce.number(),
			guestPasses: z.coerce.number(),
			pointsToUnlock: z.coerce.number(),
		}),
		z.null(),
	]),
});

export type UserAccountV1 = z.infer<typeof UserAccountV1>;
export const UserAccountV1 = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	rewards: z.union([TroonRewardsAccount, z.null()]),
	accessPoints: z.union([AccessPointsAccount, z.null()]),
	access: z.union([TroonAccessAccountSchema, z.null()]),
	savings: TroonAccessSavingsSchema,
	programs: z.array(TroonProgramAccountSchema),
	memberSince: Timestamp,
	currentSubscriptionStartDate: z.union([Timestamp, z.null()]),
});

export type UpdatePhoneNumberInput = z.infer<typeof UpdatePhoneNumberInput>;
export const UpdatePhoneNumberInput = z.object({
	phoneNumber: z.string(),
});

export type UpdatePhoneNumberResponse = z.infer<typeof UpdatePhoneNumberResponse>;
export const UpdatePhoneNumberResponse = z.object({
	requiresConfirmation: z.coerce.boolean(),
});

export type ConfirmPhoneNumberInput = z.infer<typeof ConfirmPhoneNumberInput>;
export const ConfirmPhoneNumberInput = z.object({
	phoneNumber: z.string(),
	confirmationCode: z.string(),
});

export type EmptyResponse = z.infer<typeof EmptyResponse>;
export const EmptyResponse = z.object({
	ok: z.coerce.boolean(),
});

export type RestUserWaiverDto = z.infer<typeof RestUserWaiverDto>;
export const RestUserWaiverDto = z.object({
	lastSignedDate: z.union([z.string(), z.null()]).optional(),
});

export type UserSettings = z.infer<typeof UserSettings>;
export const UserSettings = z.object({
	pushNotificationsEnabled: z.coerce.boolean(),
});

export type UpdateUserSettingsInput = z.infer<typeof UpdateUserSettingsInput>;
export const UpdateUserSettingsInput = z.object({
	pushNotificationsEnabled: z.coerce.boolean(),
});

export type UserDataSharingConsent = z.infer<typeof UserDataSharingConsent>;
export const UserDataSharingConsent = z.object({
	id: z.string(),
	userId: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
	optedOut: z.coerce.boolean(),
	type: z.union([z.literal('AUTO'), z.literal('SELECTED')]),
	isRegionalPrivacyLaw: z.coerce.boolean(),
});

export type UserDataSharingConsentState = z.infer<typeof UserDataSharingConsentState>;
export const UserDataSharingConsentState = z.object({
	current: z.union([UserDataSharingConsent, z.null()]),
});

export type UpdateUserDataSharingConsentInput = z.infer<typeof UpdateUserDataSharingConsentInput>;
export const UpdateUserDataSharingConsentInput = z.object({
	optedOut: z.coerce.boolean(),
	type: z.union([z.literal('AUTO'), z.literal('SELECTED')]),
	isRegionalPrivacyLaw: z.coerce.boolean(),
});

export type FacilitiesResponse = z.infer<typeof FacilitiesResponse>;
export const FacilitiesResponse = z.object({
	facilities: z.array(Facility),
	totalCount: z.coerce.number(),
	cursor: z.union([z.string(), z.undefined()]).optional(),
});

export type NearbyFacility = z.infer<typeof NearbyFacility>;
export const NearbyFacility = z.object({
	facility: Facility,
	distanceInMiles: z.coerce.number(),
});

export type NearbyFacilitiesResponse = z.infer<typeof NearbyFacilitiesResponse>;
export const NearbyFacilitiesResponse = z.object({
	facilities: z.array(NearbyFacility),
});

export type ToggleFavoriteFacilityInput = z.infer<typeof ToggleFavoriteFacilityInput>;
export const ToggleFavoriteFacilityInput = z.object({
	facilityId: z.string(),
	isFavorite: z.coerce.boolean(),
});

export type GuestPass = z.infer<typeof GuestPass>;
export const GuestPass = z.object({
	id: z.string(),
	status: GuestPassStatus,
	appliedAtBooking: z.coerce.boolean(),
	discount: z.union([Currency, z.null()]),
});

export type UserGuestPasses = z.infer<typeof UserGuestPasses>;
export const UserGuestPasses = z.object({
	available: z.array(GuestPass),
	pending: z.array(GuestPass),
	accepted: z.array(GuestPass),
	applied: z.array(GuestPass),
});

export type SiteEmbed = z.infer<typeof SiteEmbed>;
export const SiteEmbed = z.object({
	slug: z.string(),
	title: z.string(),
	facilities: z.array(Facility),
	displayConfig: z.array(
		z.union([
			z.literal('FEATURED_ACCESS_OFFERS'),
			z.literal('INLINE_ACCESS_OFFERS'),
			z.literal('INLINE_ACCESS_UPSELL'),
			z.literal('ACCESS_FILTER'),
			z.literal('RESERVE_ACCESS_RATE'),
			z.literal('GOLFNOW_OFFER_FILTER'),
			z.literal('GOLFNOW_OFFER_RATES'),
		]),
	),
});

export type PaymentToken = z.infer<typeof PaymentToken>;
export const PaymentToken = z.object({
	token: z.string(),
	tokenTypeId: z.coerce.number(),
	uploadUrl: z.string(),
});

export type PaymentSetupIntent = z.infer<typeof PaymentSetupIntent>;
export const PaymentSetupIntent = z.object({
	clientSecret: z.string(),
	customerId: z.string(),
});

export type CreditCardBrand = z.infer<typeof CreditCardBrand>;
export const CreditCardBrand = z.union([
	z.literal('VISA'),
	z.literal('MASTERCARD'),
	z.literal('AMEX'),
	z.literal('DISCOVER'),
	z.literal('OTHER'),
]);

export type AddCreditCardBody = z.infer<typeof AddCreditCardBody>;
export const AddCreditCardBody = z.object({
	lastFour: z.string(),
	brand: CreditCardBrand,
	token: z.string(),
	paymentProcessor: z.union([PaymentProcessor, z.undefined()]).optional(),
});

export type CreditCard = z.infer<typeof CreditCard>;
export const CreditCard = z.object({
	id: z.string(),
	lastFour: z.string(),
	brand: CreditCardBrand,
	paymentProcessor: z.union([PaymentProcessor, z.undefined()]).optional(),
});

export type AddCreditCardBodyV1Dto = z.infer<typeof AddCreditCardBodyV1Dto>;
export const AddCreditCardBodyV1Dto = z.object({
	lastFour: z.string(),
	brand: CreditCardBrand,
	token: z.string(),
	paymentProcessor: z.union([PaymentProcessor, z.undefined()]).optional(),
	expirationMonth: z.string(),
	expirationYear: z.string(),
});

export type CreditCardV1 = z.infer<typeof CreditCardV1>;
export const CreditCardV1 = z.object({
	id: z.string(),
	lastFour: z.string(),
	brand: CreditCardBrand,
	paymentProcessor: PaymentProcessor,
	expiryMonth: z.coerce.number(),
	expiryYear: z.coerce.number(),
});

export type CreditCardArray = z.infer<typeof CreditCardArray>;
export const CreditCardArray = z.object({
	creditCards: z.array(CreditCardV1),
});

export type Reservations = z.infer<typeof Reservations>;
export const Reservations = z.object({
	reservations: z.array(Reservation),
});

export type ReserveTeeTimeBody = z.infer<typeof ReserveTeeTimeBody>;
export const ReserveTeeTimeBody = z.object({
	reserveId: z.string(),
	creditCardId: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	source: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	troonRewardsOptIn: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
});

export type ReservationCancellationInfo = z.infer<typeof ReservationCancellationInfo>;
export const ReservationCancellationInfo = z.object({
	cancelBy: z.union([Timestamp, z.null()]),
	cancelFee: z.union([Currency, z.null()]),
	isCancellable: z.coerce.boolean(),
	displayMessage: z.string(),
});

export type ReservationPaymentInfo = z.infer<typeof ReservationPaymentInfo>;
export const ReservationPaymentInfo = z.object({
	receipt: Receipt,
	cancellationInfo: ReservationCancellationInfo,
	rewardPointsUsed: z.coerce.number(),
	rewardPointsEarned: z.coerce.number(),
	accessPointsEarned: z.coerce.number(),
});

export type RichTextSegment = z.infer<typeof RichTextSegment>;
export const RichTextSegment = z.object({
	text: z.string(),
	bold: z.coerce.boolean(),
});

export type PostBookingRedemptionOption = z.infer<typeof PostBookingRedemptionOption>;
export const PostBookingRedemptionOption = z.object({
	paymentMode: z.union([z.literal('DOLLARS'), z.literal('POINTS'), z.literal('SPLIT')]),
	label: z.string(),
	rateName: z.string(),
	cashAmount: Currency,
	pointsRequired: z.coerce.number(),
	displayAmount: z.string(),
	disabled: z.coerce.boolean(),
	paymentExpectation: z.array(RichTextSegment),
});

export type PostBookingRedemptionPreview = z.infer<typeof PostBookingRedemptionPreview>;
export const PostBookingRedemptionPreview = z.object({
	paymentMode: z.union([z.literal('DOLLARS'), z.literal('POINTS'), z.literal('SPLIT')]),
	availablePoints: z.coerce.number(),
	options: z.array(PostBookingRedemptionOption),
});

export type PostBookingRedemptionSubmitBody = z.infer<typeof PostBookingRedemptionSubmitBody>;
export const PostBookingRedemptionSubmitBody = z.object({
	paymentMode: z.union([z.literal('DOLLARS'), z.literal('POINTS'), z.literal('SPLIT')]),
});

export type PostBookingRedemption = z.infer<typeof PostBookingRedemption>;
export const PostBookingRedemption = z.object({
	reservationUserId: z.string(),
	paymentMode: z.union([z.literal('DOLLARS'), z.literal('POINTS'), z.literal('SPLIT')]),
	pointsRedeemed: z.coerce.number(),
	cashAmount: Currency,
	reimbursementAmount: z.union([Currency, z.null()]),
});

export type ReservationSuggestedUser = z.infer<typeof ReservationSuggestedUser>;
export const ReservationSuggestedUser = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
});

export type ReservationSuggestedUsers = z.infer<typeof ReservationSuggestedUsers>;
export const ReservationSuggestedUsers = z.object({
	users: z.array(ReservationSuggestedUser),
});

export type InviteUserToReservation = z.infer<typeof InviteUserToReservation>;
export const InviteUserToReservation = z.object({
	userId: z.string(),
});

export type DeactivateGuestPassResponse = z.infer<typeof DeactivateGuestPassResponse>;
export const DeactivateGuestPassResponse = z.object({
	guestPass: GuestPass,
	reservation: Reservation,
});

export type Coordinates = z.infer<typeof Coordinates>;
export const Coordinates = z.object({
	latitude: z.coerce.number(),
	longitude: z.coerce.number(),
});

export type Place = z.infer<typeof Place>;
export const Place = z.object({
	name: z.string(),
	coordinates: Coordinates,
	regionName: z.string(),
	countryName: z.string(),
	facilityCount: z.union([z.coerce.number(), z.undefined()]).optional(),
	troonAccessFacilityCount: z.union([z.coerce.number(), z.undefined()]).optional(),
	heroMediaId: z.union([z.string(), z.null()]),
	heroUrl: z.union([z.string(), z.null()]),
});

export type Region = z.infer<typeof Region>;
export const Region = z.object({
	id: z.string(),
	name: z.string(),
	heroMediaId: z.union([z.string(), z.null()]),
	heroUrl: z.union([z.string(), z.null()]),
	countryId: z.string(),
	countryName: z.string(),
	facilityCount: z.coerce.number(),
	troonAccessFacilityCount: z.coerce.number(),
});

export type MacroRegion = z.infer<typeof MacroRegion>;
export const MacroRegion = z.object({
	id: z.string(),
	name: z.string(),
	heroMediaId: z.union([z.string(), z.null()]),
	heroUrl: z.union([z.string(), z.null()]),
	facilityCount: z.coerce.number(),
	troonAccessFacilityCount: z.coerce.number(),
});

export type SearchResponse = z.infer<typeof SearchResponse>;
export const SearchResponse = z.object({
	facilities: z.array(Facility),
	places: z.array(Place),
	regions: z.array(Region),
	macroRegions: z.array(MacroRegion),
});

export type PopularDestinationsResponse = z.infer<typeof PopularDestinationsResponse>;
export const PopularDestinationsResponse = z.object({
	places: z.array(Place),
	regions: z.array(Region),
	macroRegions: z.array(MacroRegion),
});

export type DayString = z.infer<typeof DayString>;
export const DayString = z.string();

export type UserLocalCardMembership = z.infer<typeof UserLocalCardMembership>;
export const UserLocalCardMembership = z.object({
	cardId: z.string(),
	cardName: z.string(),
	membershipId: z.string(),
	membershipNumber: z.union([z.string(), z.null()]),
	expirationDate: z.union([DayString, z.null()]),
});

export type UserLocalCardMembershipList = z.infer<typeof UserLocalCardMembershipList>;
export const UserLocalCardMembershipList = z.object({
	memberships: z.array(UserLocalCardMembership),
});

export type StoreCreditTransferOptionsResponseDto = z.infer<typeof StoreCreditTransferOptionsResponseDto>;
export const StoreCreditTransferOptionsResponseDto = z.object({
	pointsPerDollar: z.coerce.number(),
	ratio: z.coerce.number(),
	options: z.array(
		z.object({
			amountUSD: z.coerce.number(),
			pointsRequired: z.coerce.number(),
		}),
	),
});

export type StoreCreditTransferBodyDto = z.infer<typeof StoreCreditTransferBodyDto>;
export const StoreCreditTransferBodyDto = z.object({
	amountUSD: z.coerce.number(),
});

export type StoreCreditTransferResponseDto = z.infer<typeof StoreCreditTransferResponseDto>;
export const StoreCreditTransferResponseDto = z.object({
	accessRewardsTransactionId: z.string(),
	amountUSD: z.coerce.number(),
	pointsRedeemed: z.coerce.number(),
	storeCreditTransaction: z.object({
		id: z.string(),
		amount: z.object({
			amount: z.string(),
			currencyCode: z.string(),
		}),
		account: z.object({
			id: z.string(),
			balance: z.object({
				amount: z.string(),
				currencyCode: z.string(),
			}),
		}),
	}),
});

export type TeeTimeAlertState = z.infer<typeof TeeTimeAlertState>;
export const TeeTimeAlertState = z.union([z.literal('ACTIVE'), z.literal('PAUSED'), z.literal('DELETED')]);

export type CalendarTime = z.infer<typeof CalendarTime>;
export const CalendarTime = z.object({
	hour: z.coerce.number(),
	minute: z.coerce.number(),
	second: z.union([z.coerce.number(), z.undefined()]).optional(),
});

export type FacilityTeeTimeAlertTrigger = z.infer<typeof FacilityTeeTimeAlertTrigger>;
export const FacilityTeeTimeAlertTrigger = z.object({
	facility: Facility,
	courseIds: z.array(z.string()),
	day: CalendarDay,
	startAt: z.union([CalendarTime, z.undefined()]).optional(),
	endAt: z.union([CalendarTime, z.undefined()]).optional(),
	players: z.union([z.coerce.number(), z.undefined()]).optional(),
});

export type TeeTimeAlert = z.infer<typeof TeeTimeAlert>;
export const TeeTimeAlert = z.object({
	id: z.string(),
	state: TeeTimeAlertState,
	pushEnabled: z.coerce.boolean(),
	emailEnabled: z.coerce.boolean(),
	smsEnabled: z.coerce.boolean(),
	facilityTrigger: FacilityTeeTimeAlertTrigger,
});

export type TeeTimeAlertsResponse = z.infer<typeof TeeTimeAlertsResponse>;
export const TeeTimeAlertsResponse = z.object({
	alerts: z.array(TeeTimeAlert),
});

export type CreateFacilityTeeTimeAlertInput = z.infer<typeof CreateFacilityTeeTimeAlertInput>;
export const CreateFacilityTeeTimeAlertInput = z.object({
	day: CalendarDay,
	startAt: z.union([CalendarTime, z.null()]),
	endAt: z.union([CalendarTime, z.null()]),
	players: z.union([z.coerce.number(), z.null()]),
	courseIds: z.array(z.string()),
	pushEnabled: z.coerce.boolean(),
	emailEnabled: z.coerce.boolean(),
	smsEnabled: z.coerce.boolean(),
});

export type UpdateFacilityTeeTimeAlertInput = z.infer<typeof UpdateFacilityTeeTimeAlertInput>;
export const UpdateFacilityTeeTimeAlertInput = z.object({
	day: CalendarDay.optional(),
	startAt: z.union([CalendarTime, z.null()]).optional(),
	endAt: z.union([CalendarTime, z.null()]).optional(),
	players: z.union([z.coerce.number(), z.null()]).optional(),
	courseIds: z.array(z.string()).optional(),
	pushEnabled: z.coerce.boolean().optional(),
	emailEnabled: z.coerce.boolean().optional(),
	smsEnabled: z.coerce.boolean().optional(),
});

export type DevicePushStatus = z.infer<typeof DevicePushStatus>;
export const DevicePushStatus = z.union([z.literal('NOT_DETERMINED'), z.literal('GRANTED'), z.literal('DENIED')]);

export type DeviceLocationStatus = z.infer<typeof DeviceLocationStatus>;
export const DeviceLocationStatus = z.union([
	z.literal('NOT_DETERMINED'),
	z.literal('ALWAYS_ALLOWED'),
	z.literal('ALLOWED_WHEN_OPEN'),
	z.literal('DENIED'),
]);

export type UpdateDeviceInput = z.infer<typeof UpdateDeviceInput>;
export const UpdateDeviceInput = z.object({
	deviceType: DeviceType,
	apnsToken: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	fcmToken: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
	pushStatus: z.union([z.union([DevicePushStatus, z.null()]), z.undefined()]).optional(),
	locationStatus: z.union([z.union([DeviceLocationStatus, z.null()]), z.undefined()]).optional(),
});

export type Device = z.infer<typeof Device>;
export const Device = z.object({
	id: z.string(),
	deviceId: z.string(),
	deviceType: DeviceType,
	apnsToken: z.union([z.string(), z.null()]),
	fcmToken: z.union([z.string(), z.null()]),
	pushStatus: z.union([DevicePushStatus, z.null()]),
	locationStatus: z.union([DeviceLocationStatus, z.null()]),
	userId: z.union([z.string(), z.null()]),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type CreateSupportRequest = z.infer<typeof CreateSupportRequest>;
export const CreateSupportRequest = z.object({
	message: z.string(),
	name: z.union([z.string(), z.undefined()]).optional(),
	email: z.union([z.string(), z.undefined()]).optional(),
	platform: z.union([z.string(), z.undefined()]).optional(),
	version: z.union([z.string(), z.undefined()]).optional(),
});

export type TroonPriveRequest = z.infer<typeof TroonPriveRequest>;
export const TroonPriveRequest = z.object({
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
	phone: z.string(),
	homeFacilityId: z.string(),
	requestedFacilityId: z.string(),
	homeMembershipNumber: z.string(),
	dayTime: CalendarDayTime,
	members: z.union([z.array(z.string()), z.undefined()]).optional(),
	guests: z.union([z.array(z.string()), z.undefined()]).optional(),
	comments: z.union([z.string(), z.undefined()]).optional(),
});

export type FacilitySortField = z.infer<typeof FacilitySortField>;
export const FacilitySortField = z.union([z.literal('name'), z.literal('cursor')]);

export type SortDirection = z.infer<typeof SortDirection>;
export const SortDirection = z.union([z.literal('asc'), z.literal('desc')]);

export type post_AuthenticationController_getWebSession_v0 = typeof post_AuthenticationController_getWebSession_v0;
export const post_AuthenticationController_getWebSession_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/get-web-session'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: GetWebSessionBody,
	}),
	responses: z.object({
		'200': SuccessResponseDto,
	}),
};

export type post_AuthenticationController_login_v0 = typeof post_AuthenticationController_login_v0;
export const post_AuthenticationController_login_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/login'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: LoginBody,
	}),
	responses: z.object({
		'200': LoginResponse,
	}),
};

export type post_AuthenticationController_restSignup_v0 = typeof post_AuthenticationController_restSignup_v0;
export const post_AuthenticationController_restSignup_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/signup'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: SignupBody,
	}),
	responses: z.object({
		'200': SignupResponse,
	}),
};

export type post_AuthenticationController_refreshToken_v0 = typeof post_AuthenticationController_refreshToken_v0;
export const post_AuthenticationController_refreshToken_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/refresh-token'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: RefreshTokenBody,
	}),
	responses: z.object({
		'200': RefreshTokenResponse,
	}),
};

export type post_AuthenticationController_emailMagicLink_v0 = typeof post_AuthenticationController_emailMagicLink_v0;
export const post_AuthenticationController_emailMagicLink_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/magic-link/email'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: EmailMagicLinkBody,
	}),
	responses: z.object({
		'200': SuccessResponseDto,
	}),
};

export type post_AuthenticationController_confirmEmailMagicLink_v0 =
	typeof post_AuthenticationController_confirmEmailMagicLink_v0;
export const post_AuthenticationController_confirmEmailMagicLink_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/magic-link/confirm'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ConfirmMagicLinkBody,
	}),
	responses: z.object({
		'200': LoginResponse,
	}),
};

export type post_AuthenticationController_forgotPassword_v0 = typeof post_AuthenticationController_forgotPassword_v0;
export const post_AuthenticationController_forgotPassword_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/forgot-password'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ForgotPasswordBody,
	}),
	responses: z.object({
		'200': ForgotPasswordResponse,
	}),
};

export type post_AuthenticationController_resetPassword_v0 = typeof post_AuthenticationController_resetPassword_v0;
export const post_AuthenticationController_resetPassword_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/reset-password'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ResetPasswordBody,
	}),
	responses: z.object({
		'200': ResetPasswordResponse,
	}),
};

export type post_AuthenticationController_logout_v0 = typeof post_AuthenticationController_logout_v0;
export const post_AuthenticationController_logout_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/auth/logout'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: LogoutBody,
	}),
	responses: z.object({
		'200': LogoutResponse,
	}),
};

export type get_AuthenticationController_me_v0 = typeof get_AuthenticationController_me_v0;
export const get_AuthenticationController_me_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/auth/me'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': AuthUser,
	}),
};

export type patch_AuthenticationController_updateMe_v0 = typeof patch_AuthenticationController_updateMe_v0;
export const patch_AuthenticationController_updateMe_v0 = {
	method: z.literal('PATCH'),
	path: z.literal('/v0/auth/me'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: UpdateMeBody,
	}),
	responses: z.object({
		'200': AuthUser,
	}),
};

export type get_AccessRewardsController_getConversionPreview_v0 =
	typeof get_AccessRewardsController_getConversionPreview_v0;
export const get_AccessRewardsController_getConversionPreview_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/access-rewards/conversion-preview'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': PointsConversionPreview,
	}),
};

export type post_AccessRewardsController_confirmConversion_v0 =
	typeof post_AccessRewardsController_confirmConversion_v0;
export const post_AccessRewardsController_confirmConversion_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/access-rewards/conversion-confirm'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': PointsConversionConfirmResponse,
	}),
};

export type get_AccessRewardsController_getUserTransactions_v0 =
	typeof get_AccessRewardsController_getUserTransactions_v0;
export const get_AccessRewardsController_getUserTransactions_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/access-rewards/transactions'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserTransactionsResponse,
	}),
};

export type get_ProgramsController_getSignupProgram_v0 = typeof get_ProgramsController_getSignupProgram_v0;
export const get_ProgramsController_getSignupProgram_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/rewards/programs/{programSignUpType}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			programSignUpType: z.union([
				z.literal('BAYCLUB'),
				z.literal('VILLAGE_GAINEY'),
				z.literal('VILLAGE_CAMELBACK'),
				z.literal('VILLAGE_DC_RANCH'),
				z.literal('VILLAGE_OCOTILLO'),
			]),
		}),
	}),
	responses: z.object({
		'200': ProgramSignupInfoResponse,
	}),
};

export type post_ProgramsController_enrollSignupProgram_v0 = typeof post_ProgramsController_enrollSignupProgram_v0;
export const post_ProgramsController_enrollSignupProgram_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/rewards/programs/{programSignUpType}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			programSignUpType: z.union([
				z.literal('BAYCLUB'),
				z.literal('VILLAGE_GAINEY'),
				z.literal('VILLAGE_CAMELBACK'),
				z.literal('VILLAGE_DC_RANCH'),
				z.literal('VILLAGE_OCOTILLO'),
			]),
		}),
	}),
	responses: z.object({
		'200': ProgramSignupEnrollResponse,
	}),
};

export type post_SubscriptionAddonController_previewAddon_v0 = typeof post_SubscriptionAddonController_previewAddon_v0;
export const post_SubscriptionAddonController_previewAddon_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/subscription-addons/preview'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: AddonPreviewRequestBody,
	}),
	responses: z.object({
		'200': AddonPreviewResponse,
	}),
};

export type post_SubscriptionAddonController_purchaseAddon_v0 =
	typeof post_SubscriptionAddonController_purchaseAddon_v0;
export const post_SubscriptionAddonController_purchaseAddon_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/subscription-addons/purchase'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: AddAddonPurchaseRequestBodyDto,
	}),
	responses: z.object({
		'200': AddonPurchaseResponse,
	}),
};

export type get_SubscriptionAddonController_getAddonProducts_v0 =
	typeof get_SubscriptionAddonController_getAddonProducts_v0;
export const get_SubscriptionAddonController_getAddonProducts_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/subscription-addons/products'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': GetAddonProductsResponse,
	}),
};

export type get_TeeTimesController_getCourseTeeTimes_v0 = typeof get_TeeTimesController_getCourseTeeTimes_v0;
export const get_TeeTimesController_getCourseTeeTimes_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/tee-times'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			day: z.string(),
			facilityId: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			courseIds: z.union([z.union([z.union([z.array(z.string()), z.string()]), z.null()]), z.undefined()]).optional(),
			players: z.union([z.union([z.coerce.number(), z.null()]), z.undefined()]).optional(),
			startAt: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			endAt: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			includeCart: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
			troonAccessPlusDeal: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
			hotDeals: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
			group: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmCampaign: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmSource: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmMedium: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmTerm: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmContent: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
		}),
	}),
	responses: z.object({
		'200': TeeTimesResponse,
	}),
};

export type get_TeeTimesController_getCourseTeeTimesV1_v1 = typeof get_TeeTimesController_getCourseTeeTimesV1_v1;
export const get_TeeTimesController_getCourseTeeTimesV1_v1 = {
	method: z.literal('GET'),
	path: z.literal('/v1/tee-times'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			day: z.string(),
			facilityId: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			courseIds: z.union([z.union([z.union([z.array(z.string()), z.string()]), z.null()]), z.undefined()]).optional(),
			players: z.union([z.union([z.coerce.number(), z.null()]), z.undefined()]).optional(),
			startAt: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			endAt: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			includeCart: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
			troonAccessPlusDeal: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
			hotDeals: z.union([z.union([z.coerce.boolean(), z.null()]), z.undefined()]).optional(),
			group: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmCampaign: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmSource: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmMedium: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmTerm: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
			utmContent: z.union([z.union([z.string(), z.null()]), z.undefined()]).optional(),
		}),
	}),
	responses: z.object({
		'200': TeeTimesResponseDtoV1,
	}),
};

export type get_TeeTimesController_getTeeTimeReservationInfo_v0 =
	typeof get_TeeTimesController_getTeeTimeReservationInfo_v0;
export const get_TeeTimesController_getTeeTimeReservationInfo_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/tee-times/{teeTimeId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			rateId: z.union([z.string(), z.null()]).optional(),
			players: z.union([z.coerce.number(), z.null()]).optional(),
			utmCampaign: z.union([z.string(), z.null()]).optional(),
			utmSource: z.union([z.string(), z.null()]).optional(),
			utmMedium: z.union([z.string(), z.null()]).optional(),
			utmTerm: z.union([z.string(), z.null()]).optional(),
			utmContent: z.union([z.string(), z.null()]).optional(),
		}),
		path: z.object({
			teeTimeId: z.string(),
		}),
	}),
	responses: z.object({
		'200': TeeTimeReservationInfo,
	}),
};

export type get_TeeTimesController_getTeeTimeReservationInfoV1_v1 =
	typeof get_TeeTimesController_getTeeTimeReservationInfoV1_v1;
export const get_TeeTimesController_getTeeTimeReservationInfoV1_v1 = {
	method: z.literal('GET'),
	path: z.literal('/v1/tee-times/{teeTimeId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			rateId: z.union([z.string(), z.null()]).optional(),
			players: z.union([z.coerce.number(), z.null()]).optional(),
			guestPassCount: z.union([z.coerce.number(), z.null()]).optional(),
			payMode: z.union([z.union([z.literal('dollars'), z.literal('points'), z.literal('split')]), z.null()]).optional(),
			redeemForAll: z.union([z.coerce.boolean(), z.null()]).optional(),
			utmCampaign: z.union([z.string(), z.null()]).optional(),
			utmSource: z.union([z.string(), z.null()]).optional(),
			utmMedium: z.union([z.string(), z.null()]).optional(),
			utmTerm: z.union([z.string(), z.null()]).optional(),
			utmContent: z.union([z.string(), z.null()]).optional(),
		}),
		path: z.object({
			teeTimeId: z.string(),
		}),
	}),
	responses: z.object({
		'200': TeeTimeReservationInfoDtoV1,
	}),
};

export type get_TroonAccessController_getPaymentMethods_v0 = typeof get_TroonAccessController_getPaymentMethods_v0;
export const get_TroonAccessController_getPaymentMethods_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access/payment-methods'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': StripePaymentMethods,
	}),
};

export type post_TroonAccessController_addPaymentMethod_v0 = typeof post_TroonAccessController_addPaymentMethod_v0;
export const post_TroonAccessController_addPaymentMethod_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/troon-access/payment-methods'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: AddPaymentMethodBody,
	}),
	responses: z.object({
		'200': AddPaymentMethodResponse,
	}),
};

export type get_TroonAccessSavingsController_getReservationUpsell_v0 =
	typeof get_TroonAccessSavingsController_getReservationUpsell_v0;
export const get_TroonAccessSavingsController_getReservationUpsell_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-savings/reservations/{reservationId}/upsell'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
		}),
	}),
	responses: z.object({
		'200': TroonAccessReservationUpsellResponse,
	}),
};

export type get_TroonAccessSavingsController_getTotalSavings_v0 =
	typeof get_TroonAccessSavingsController_getTotalSavings_v0;
export const get_TroonAccessSavingsController_getTotalSavings_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-savings/total'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': TroonAccessTotalSavingsResponse,
	}),
};

export type get_TroonAccessSavingsController_getDiscountsFeed_v0 =
	typeof get_TroonAccessSavingsController_getDiscountsFeed_v0;
export const get_TroonAccessSavingsController_getDiscountsFeed_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-savings/discounts-feed'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': TroonAccessReservationSavingsList,
	}),
};

export type get_TroonAccessSavingsController_getAccessPremiumTeeTimeDeals_v0 =
	typeof get_TroonAccessSavingsController_getAccessPremiumTeeTimeDeals_v0;
export const get_TroonAccessSavingsController_getAccessPremiumTeeTimeDeals_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-savings/access-premium-tee-time-deals'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			sort: z.union([z.literal('dayTime'), z.literal('discount')]).optional(),
			courseLimit: z.coerce.number().optional(),
			teeTimeLimit: z.coerce.number().optional(),
		}),
	}),
	responses: z.object({
		'200': AccessPremiumTeeTimeDealsResponse,
	}),
};

export type get_TroonAccessSubscriptionsController_getActiveSubscription_v0 =
	typeof get_TroonAccessSubscriptionsController_getActiveSubscription_v0;
export const get_TroonAccessSubscriptionsController_getActiveSubscription_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-subscriptions/active'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': TroonAccessSubscription,
	}),
};

export type get_TroonAccessSubscriptionsController_getProducts_v0 =
	typeof get_TroonAccessSubscriptionsController_getProducts_v0;
export const get_TroonAccessSubscriptionsController_getProducts_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-subscriptions/products'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			promoCode: z.union([z.string(), z.null()]).optional(),
			includeAccessPremiumElite: z.union([z.coerce.boolean(), z.null()]).optional(),
		}),
	}),
	responses: z.object({
		'200': TroonAccessSubscriptionProductsResponse,
	}),
};

export type get_TroonAccessSubscriptionsController_getProduct_v0 =
	typeof get_TroonAccessSubscriptionsController_getProduct_v0;
export const get_TroonAccessSubscriptionsController_getProduct_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/troon-access-subscriptions/products/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			promoCode: z.string().optional(),
		}),
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': TroonAccessSubscriptionProduct,
	}),
};

export type post_TroonAccessSubscriptionsController_setupSubscription_v0 =
	typeof post_TroonAccessSubscriptionsController_setupSubscription_v0;
export const post_TroonAccessSubscriptionsController_setupSubscription_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/troon-access-subscriptions/setup'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			utmCampaign: z.union([z.string(), z.null()]).optional(),
			utmSource: z.union([z.string(), z.null()]).optional(),
			utmMedium: z.union([z.string(), z.null()]).optional(),
			utmTerm: z.union([z.string(), z.null()]).optional(),
			utmContent: z.union([z.string(), z.null()]).optional(),
		}),
		body: SetupTroonAccessSubscriptionBody,
	}),
	responses: z.object({
		'200': TroonAccessSubscriptionIntent,
	}),
};

export type post_TroonAccessSubscriptionsController_confirmSubscriptionPayment_v0 =
	typeof post_TroonAccessSubscriptionsController_confirmSubscriptionPayment_v0;
export const post_TroonAccessSubscriptionsController_confirmSubscriptionPayment_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/troon-access-subscriptions/confirm'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ConfirmTroonAccessSubscriptionBody,
	}),
	responses: z.object({
		'200': TroonAccessSubscription,
	}),
};

export type post_TroonAccessSubscriptionsController_previewUpgrade_v0 =
	typeof post_TroonAccessSubscriptionsController_previewUpgrade_v0;
export const post_TroonAccessSubscriptionsController_previewUpgrade_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/troon-access-subscriptions/upgrade/preview'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: PreviewAccessPremiumMembershipUpgradeBody,
	}),
	responses: z.object({
		'200': AccessPremiumMembershipUpgradePreviewResponse,
	}),
};

export type post_TroonAccessSubscriptionsController_confirmUpgrade_v0 =
	typeof post_TroonAccessSubscriptionsController_confirmUpgrade_v0;
export const post_TroonAccessSubscriptionsController_confirmUpgrade_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/troon-access-subscriptions/upgrade/confirm'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ConfirmAccessPremiumMembershipUpgradeBody,
	}),
	responses: z.object({
		'200': AccessPremiumMembershipUpgradeConfirmResponse,
	}),
};

export type get_VisaController_getVisaStatus_v0 = typeof get_VisaController_getVisaStatus_v0;
export const get_VisaController_getVisaStatus_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/visa/status'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': VisaStatusResponse,
	}),
};

export type get_VisaController_verifyVisaRequest_v0 = typeof get_VisaController_verifyVisaRequest_v0;
export const get_VisaController_verifyVisaRequest_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/visa/tokens/{token}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			token: z.string(),
		}),
	}),
	responses: z.object({
		'200': VisaTokenResult,
	}),
};

export type post_VisaController_getVisaToken_v0 = typeof post_VisaController_getVisaToken_v0;
export const post_VisaController_getVisaToken_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/visa/tokens'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': VisaToken,
	}),
};

export type get_VisaController_getPriveRequests_v0 = typeof get_VisaController_getPriveRequests_v0;
export const get_VisaController_getPriveRequests_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/visa/prive/requests'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': PriveTeeTimeRequest,
	}),
};

export type post_VisaController_createVisaPriveRequest_v0 = typeof post_VisaController_createVisaPriveRequest_v0;
export const post_VisaController_createVisaPriveRequest_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/visa/prive/requests'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: PriveRequestBody,
	}),
	responses: z.object({
		'200': PriveTeeTimeRequest,
	}),
};

export type get_UsersController_userExists_v0 = typeof get_UsersController_userExists_v0;
export const get_UsersController_userExists_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/users/exists'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			email: z.string(),
		}),
		header: z.object({
			'x-token': z.string().optional(),
		}),
	}),
	responses: z.object({
		'200': UserExistsResponse,
	}),
};

export type get_UsersController_userEmailExists_v0 = typeof get_UsersController_userEmailExists_v0;
export const get_UsersController_userEmailExists_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/users/email-exists'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			email: z.string(),
		}),
		header: z.object({
			'x-token': z.string().optional(),
		}),
	}),
	responses: z.object({
		'200': UserExistsResponse,
	}),
};

export type get_UsersController_getUserAccount_v0 = typeof get_UsersController_getUserAccount_v0;
export const get_UsersController_getUserAccount_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/users/account'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserAccount,
	}),
};

export type delete_UsersController_deleteAccount_v0 = typeof delete_UsersController_deleteAccount_v0;
export const delete_UsersController_deleteAccount_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/users/account'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': SuccessResponseDto,
	}),
};

export type get_UsersController_getUserAccountV1_v1 = typeof get_UsersController_getUserAccountV1_v1;
export const get_UsersController_getUserAccountV1_v1 = {
	method: z.literal('GET'),
	path: z.literal('/v1/users/account'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserAccountV1,
	}),
};

export type post_UsersController_updatePhone_v0 = typeof post_UsersController_updatePhone_v0;
export const post_UsersController_updatePhone_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/users/account/phone'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: UpdatePhoneNumberInput,
	}),
	responses: z.object({
		'200': UpdatePhoneNumberResponse,
	}),
};

export type post_UsersController_confirmPhone_v0 = typeof post_UsersController_confirmPhone_v0;
export const post_UsersController_confirmPhone_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/users/account/phone/confirm'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ConfirmPhoneNumberInput,
	}),
	responses: z.object({
		'200': EmptyResponse,
	}),
};

export type get_UsersController_waiver_v0 = typeof get_UsersController_waiver_v0;
export const get_UsersController_waiver_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/users/account/waiver'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': RestUserWaiverDto,
	}),
};

export type post_UsersController_signWaiver_v0 = typeof post_UsersController_signWaiver_v0;
export const post_UsersController_signWaiver_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/users/account/waiver'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': RestUserWaiverDto,
	}),
};

export type get_UsersController_getUserSettings_v0 = typeof get_UsersController_getUserSettings_v0;
export const get_UsersController_getUserSettings_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/users/account/settings'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserSettings,
	}),
};

export type patch_UsersController_updateUserSettings_v0 = typeof patch_UsersController_updateUserSettings_v0;
export const patch_UsersController_updateUserSettings_v0 = {
	method: z.literal('PATCH'),
	path: z.literal('/v0/users/account/settings'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: UpdateUserSettingsInput,
	}),
	responses: z.object({
		'200': UserSettings,
	}),
};

export type get_UsersController_getDataSharingConsent_v0 = typeof get_UsersController_getDataSharingConsent_v0;
export const get_UsersController_getDataSharingConsent_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/users/account/data-sharing-consent'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserDataSharingConsentState,
	}),
};

export type post_UsersController_updateDataSharingConsent_v0 = typeof post_UsersController_updateDataSharingConsent_v0;
export const post_UsersController_updateDataSharingConsent_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/users/account/data-sharing-consent'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: UpdateUserDataSharingConsentInput,
	}),
	responses: z.object({
		'200': UserDataSharingConsent,
	}),
};

export type get_FacilitiesController_getFacilities_v0 = typeof get_FacilitiesController_getFacilities_v0;
export const get_FacilitiesController_getFacilities_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/facilities'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			idOrSlugs: z.union([z.string(), z.array(z.string())]).optional(),
			regionIds: z.union([z.string(), z.array(z.string())]).optional(),
			countryIds: z.union([z.string(), z.array(z.string())]).optional(),
			macroRegionIds: z.union([z.string(), z.array(z.string())]).optional(),
			featured: z.coerce.boolean().optional(),
			featuredOnTroonCom: z.coerce.boolean().optional(),
			featuredOnGolfWithAccess: z.coerce.boolean().optional(),
			types: z.union([TroonFacilityType, z.array(TroonFacilityType)]).optional(),
			brands: z.union([TroonFacilityBrand, z.array(TroonFacilityBrand)]).optional(),
			supportsTroonAccess: z.coerce.boolean().optional(),
			supportsTroonRewards: z.coerce.boolean().optional(),
			supportsVisaPrive: z.coerce.boolean().optional(),
			supportsTroonAccessPrive: z.coerce.boolean().optional(),
			supportsTroonAdvantage: z.coerce.boolean().optional(),
			supportsAccessRewards: z.coerce.boolean().optional(),
			supportsAccessPoints: z.coerce.boolean().optional(),
			nearby: z.coerce.boolean().optional(),
			location: z
				.object({
					latitude: z.coerce.number(),
					longitude: z.coerce.number(),
					radiusMiles: z.union([z.coerce.number(), z.undefined()]).optional(),
				})
				.optional(),
			favorite: z.coerce.boolean().optional(),
			allowsOutgoingPriveRequests: z.coerce.boolean().optional(),
			allowsIncomingPriveRequests: z.coerce.boolean().optional(),
			isBookable: z.coerce.boolean().optional(),
			troonAccessPlusDeal: z.coerce.boolean().optional(),
			limit: z.coerce.number().optional(),
			cursor: z.string().optional(),
			sortBy: z.union([z.literal('name'), z.literal('cursor')]).optional(),
			sortDirection: z.union([z.literal('asc'), z.literal('desc')]).optional(),
		}),
	}),
	responses: z.object({
		'200': FacilitiesResponse,
	}),
};

export type get_FacilitiesController_getNearbyFacilities_v0 = typeof get_FacilitiesController_getNearbyFacilities_v0;
export const get_FacilitiesController_getNearbyFacilities_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/facilities/nearby'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			latitude: z.coerce.number().optional(),
			longitude: z.coerce.number().optional(),
			radiusInMiles: z.coerce.number().optional(),
			isBookable: z.coerce.boolean().optional(),
			limit: z.coerce.number().optional(),
		}),
	}),
	responses: z.object({
		'200': NearbyFacilitiesResponse,
	}),
};

export type get_FacilitiesController_getFavoriteFacilities_v0 =
	typeof get_FacilitiesController_getFavoriteFacilities_v0;
export const get_FacilitiesController_getFavoriteFacilities_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/facilities/favorites'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': FacilitiesResponse,
	}),
};

export type post_FacilitiesController_toggleFavoriteFacility_v0 =
	typeof post_FacilitiesController_toggleFavoriteFacility_v0;
export const post_FacilitiesController_toggleFavoriteFacility_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/facilities/toggle-favorite'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: ToggleFavoriteFacilityInput,
	}),
	responses: z.object({
		'200': Facility,
	}),
};

export type get_FacilitiesController_getFacility_v0 = typeof get_FacilitiesController_getFacility_v0;
export const get_FacilitiesController_getFacility_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/facilities/{idOrSlug}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			idOrSlug: z.string(),
		}),
	}),
	responses: z.object({
		'200': Facility,
	}),
};

export type get_GuestPassesController_getGuestPasses_v0 = typeof get_GuestPassesController_getGuestPasses_v0;
export const get_GuestPassesController_getGuestPasses_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/guest-passes'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserGuestPasses,
	}),
};

export type get_SiteEmbedsController_getEmbed_v0 = typeof get_SiteEmbedsController_getEmbed_v0;
export const get_SiteEmbedsController_getEmbed_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/site-embeds/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': SiteEmbed,
	}),
};

export type post_PaymentsController_createPaymentToken_v0 = typeof post_PaymentsController_createPaymentToken_v0;
export const post_PaymentsController_createPaymentToken_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/payments/token'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': PaymentToken,
	}),
};

export type post_PaymentsController_createStripeSetupIntent_v0 =
	typeof post_PaymentsController_createStripeSetupIntent_v0;
export const post_PaymentsController_createStripeSetupIntent_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/payments/intent'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': PaymentSetupIntent,
	}),
};

export type post_PaymentsController_addCreditCard_v0 = typeof post_PaymentsController_addCreditCard_v0;
export const post_PaymentsController_addCreditCard_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/payments/credit-cards'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: AddCreditCardBody,
	}),
	responses: z.object({
		'200': CreditCard,
	}),
};

export type get_PaymentsController_getCreditCards_v0 = typeof get_PaymentsController_getCreditCards_v0;
export const get_PaymentsController_getCreditCards_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/payments/credit-cards'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': CreditCardArray,
	}),
};

export type post_PaymentsController_addCreditCardV1_v1 = typeof post_PaymentsController_addCreditCardV1_v1;
export const post_PaymentsController_addCreditCardV1_v1 = {
	method: z.literal('POST'),
	path: z.literal('/v1/payments/credit-cards'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: AddCreditCardBodyV1Dto,
	}),
	responses: z.object({
		'200': CreditCardV1,
	}),
};

export type delete_PaymentsController_deleteCreditCard_v0 = typeof delete_PaymentsController_deleteCreditCard_v0;
export const delete_PaymentsController_deleteCreditCard_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/payments/credit-cards/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': SuccessResponseDto,
	}),
};

export type get_ReservationsController_getReservationInvites_v0 =
	typeof get_ReservationsController_getReservationInvites_v0;
export const get_ReservationsController_getReservationInvites_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/reservations/invites'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': Reservations,
	}),
};

export type get_ReservationsController_getReservationById_v0 = typeof get_ReservationsController_getReservationById_v0;
export const get_ReservationsController_getReservationById_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/reservations/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type post_ReservationsController_reserveTeeTime_v0 = typeof post_ReservationsController_reserveTeeTime_v0;
export const post_ReservationsController_reserveTeeTime_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/reservations'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			utmCampaign: z.union([z.string(), z.null()]).optional(),
			utmSource: z.union([z.string(), z.null()]).optional(),
			utmMedium: z.union([z.string(), z.null()]).optional(),
			utmTerm: z.union([z.string(), z.null()]).optional(),
			utmContent: z.union([z.string(), z.null()]).optional(),
		}),
		body: ReserveTeeTimeBody,
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type get_ReservationsController_getAllReservations_v0 = typeof get_ReservationsController_getAllReservations_v0;
export const get_ReservationsController_getAllReservations_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/reservations'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			sortOrder: z.union([z.literal('ASC'), z.literal('DESC')]).optional(),
			upcoming: z.coerce.boolean().optional(),
			past: z.coerce.boolean().optional(),
			cancelled: z.coerce.boolean().optional(),
			limit: z.coerce.number().optional(),
		}),
	}),
	responses: z.object({
		'200': Reservations,
	}),
};

export type get_ReservationsController_getReservationPaymentInfo_v0 =
	typeof get_ReservationsController_getReservationPaymentInfo_v0;
export const get_ReservationsController_getReservationPaymentInfo_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/reservations/{id}/payment-info'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': ReservationPaymentInfo,
	}),
};

export type get_ReservationsController_getReservationPaymentInfoV1_v1 =
	typeof get_ReservationsController_getReservationPaymentInfoV1_v1;
export const get_ReservationsController_getReservationPaymentInfoV1_v1 = {
	method: z.literal('GET'),
	path: z.literal('/v1/reservations/{id}/payment-info'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': ReservationPaymentInfo,
	}),
};

export type get_ReservationsController_previewPostBookingRedemption_v1 =
	typeof get_ReservationsController_previewPostBookingRedemption_v1;
export const get_ReservationsController_previewPostBookingRedemption_v1 = {
	method: z.literal('GET'),
	path: z.literal('/v1/reservations/{reservationId}/users/{reservationUserId}/redemption-preview'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
	}),
	responses: z.object({
		'200': PostBookingRedemptionPreview,
	}),
};

export type post_ReservationsController_submitPostBookingPaymentOption_v1 =
	typeof post_ReservationsController_submitPostBookingPaymentOption_v1;
export const post_ReservationsController_submitPostBookingPaymentOption_v1 = {
	method: z.literal('POST'),
	path: z.literal('/v1/reservations/{reservationId}/users/{reservationUserId}/payment-option'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
		body: PostBookingRedemptionSubmitBody,
	}),
	responses: z.object({
		'200': PostBookingRedemption,
	}),
};

export type get_ReservationsController_getReservationInvite_v0 =
	typeof get_ReservationsController_getReservationInvite_v0;
export const get_ReservationsController_getReservationInvite_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/reservations/{reservationId}/join/{shareInviteId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			shareInviteId: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type post_ReservationsController_joinReservation_v0 = typeof post_ReservationsController_joinReservation_v0;
export const post_ReservationsController_joinReservation_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/reservations/{reservationId}/join/{shareInviteId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			shareInviteId: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type get_ReservationsController_getSuggestedReservationUsers_v0 =
	typeof get_ReservationsController_getSuggestedReservationUsers_v0;
export const get_ReservationsController_getSuggestedReservationUsers_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/reservations/{reservationId}/suggested-users'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			limit: z.coerce.number().optional(),
		}),
		path: z.object({
			reservationId: z.string(),
		}),
	}),
	responses: z.object({
		'200': ReservationSuggestedUsers,
	}),
};

export type delete_ReservationsController_cancelReservation_v0 =
	typeof delete_ReservationsController_cancelReservation_v0;
export const delete_ReservationsController_cancelReservation_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/reservations/{reservationId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type post_ReservationsController_inviteUserToReservation_v0 =
	typeof post_ReservationsController_inviteUserToReservation_v0;
export const post_ReservationsController_inviteUserToReservation_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/reservations/{reservationId}/invites/{reservationUserId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
		body: InviteUserToReservation,
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type patch_ReservationsController_acceptReservationInvite_v0 =
	typeof patch_ReservationsController_acceptReservationInvite_v0;
export const patch_ReservationsController_acceptReservationInvite_v0 = {
	method: z.literal('PATCH'),
	path: z.literal('/v0/reservations/{reservationId}/invites/{reservationUserId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type delete_ReservationsController_rejectReservationInvite_v0 =
	typeof delete_ReservationsController_rejectReservationInvite_v0;
export const delete_ReservationsController_rejectReservationInvite_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/reservations/{reservationId}/invites/{reservationUserId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
	}),
	responses: z.object({
		'200': EmptyResponse,
	}),
};

export type delete_ReservationsController_leaveReservation_v0 =
	typeof delete_ReservationsController_leaveReservation_v0;
export const delete_ReservationsController_leaveReservation_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/reservations/{reservationId}/users/{reservationUserId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
	}),
	responses: z.object({
		'200': EmptyResponse,
	}),
};

export type delete_ReservationsController_removeUserFromReservation_v0 =
	typeof delete_ReservationsController_removeUserFromReservation_v0;
export const delete_ReservationsController_removeUserFromReservation_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/reservations/{reservationId}/users/{reservationUserId}/remove'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type post_ReservationsController_addGuestPassToUser_v0 =
	typeof post_ReservationsController_addGuestPassToUser_v0;
export const post_ReservationsController_addGuestPassToUser_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/reservations/{reservationId}/users/{reservationUserId}/guest-pass'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			reservationUserId: z.string(),
		}),
	}),
	responses: z.object({
		'200': Reservation,
	}),
};

export type delete_ReservationsController_deactivateGuestPass_v0 =
	typeof delete_ReservationsController_deactivateGuestPass_v0;
export const delete_ReservationsController_deactivateGuestPass_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/reservations/{reservationId}/guest-passes/{guestPassId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			reservationId: z.string(),
			guestPassId: z.string(),
		}),
	}),
	responses: z.object({
		'200': DeactivateGuestPassResponse,
	}),
};

export type get_SearchController_search_v0 = typeof get_SearchController_search_v0;
export const get_SearchController_search_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/search'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			query: z.string(),
			isBookable: z.union([z.coerce.boolean(), z.undefined()]).optional(),
		}),
	}),
	responses: z.object({
		'200': SearchResponse,
	}),
};

export type get_SearchController_popularDestinations_v0 = typeof get_SearchController_popularDestinations_v0;
export const get_SearchController_popularDestinations_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/search/popular-destinations'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': PopularDestinationsResponse,
	}),
};

export type get_LocalCardsController_getMyLocalCards_v0 = typeof get_LocalCardsController_getMyLocalCards_v0;
export const get_LocalCardsController_getMyLocalCards_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/local-cards'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': UserLocalCardMembershipList,
	}),
};

export type get_ShopifyController_getTransferOptions_v0 = typeof get_ShopifyController_getTransferOptions_v0;
export const get_ShopifyController_getTransferOptions_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/shopify/transfer-options'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': StoreCreditTransferOptionsResponseDto,
	}),
};

export type post_ShopifyController_transferToStoreCredit_v0 = typeof post_ShopifyController_transferToStoreCredit_v0;
export const post_ShopifyController_transferToStoreCredit_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/shopify/transfer'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: StoreCreditTransferBodyDto,
	}),
	responses: z.object({
		'200': StoreCreditTransferResponseDto,
	}),
};

export type post_ShopifyController_syncCustomerByEmail_v0 = typeof post_ShopifyController_syncCustomerByEmail_v0;
export const post_ShopifyController_syncCustomerByEmail_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/shopify/sync-customer-by-email'),
	requestFormat: z.literal('json'),
	parameters: z.never(),
	responses: z.object({
		'200': AuthUser,
	}),
};

export type get_TeeTimeAlertsController_getTeeTimeAlerts_v0 = typeof get_TeeTimeAlertsController_getTeeTimeAlerts_v0;
export const get_TeeTimeAlertsController_getTeeTimeAlerts_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/tee-time-alerts'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		query: z.object({
			isActive: z.coerce.boolean().optional(),
		}),
	}),
	responses: z.object({
		'200': TeeTimeAlertsResponse,
	}),
};

export type post_TeeTimeAlertsController_createFacilityTeeTimeAlert_v0 =
	typeof post_TeeTimeAlertsController_createFacilityTeeTimeAlert_v0;
export const post_TeeTimeAlertsController_createFacilityTeeTimeAlert_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/tee-time-alerts'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: CreateFacilityTeeTimeAlertInput,
	}),
	responses: z.object({
		'200': TeeTimeAlert,
	}),
};

export type get_TeeTimeAlertsController_getTeeTimeAlert_v0 = typeof get_TeeTimeAlertsController_getTeeTimeAlert_v0;
export const get_TeeTimeAlertsController_getTeeTimeAlert_v0 = {
	method: z.literal('GET'),
	path: z.literal('/v0/tee-time-alerts/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': TeeTimeAlert,
	}),
};

export type patch_TeeTimeAlertsController_updateFacilityTeeTimeAlert_v0 =
	typeof patch_TeeTimeAlertsController_updateFacilityTeeTimeAlert_v0;
export const patch_TeeTimeAlertsController_updateFacilityTeeTimeAlert_v0 = {
	method: z.literal('PATCH'),
	path: z.literal('/v0/tee-time-alerts/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
		body: UpdateFacilityTeeTimeAlertInput,
	}),
	responses: z.object({
		'200': TeeTimeAlert,
	}),
};

export type delete_TeeTimeAlertsController_deleteTeeTimeAlert_v0 =
	typeof delete_TeeTimeAlertsController_deleteTeeTimeAlert_v0;
export const delete_TeeTimeAlertsController_deleteTeeTimeAlert_v0 = {
	method: z.literal('DELETE'),
	path: z.literal('/v0/tee-time-alerts/{id}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': TeeTimeAlert,
	}),
};

export type patch_TeeTimeAlertsController_toggleTeeTimeAlert_v0 =
	typeof patch_TeeTimeAlertsController_toggleTeeTimeAlert_v0;
export const patch_TeeTimeAlertsController_toggleTeeTimeAlert_v0 = {
	method: z.literal('PATCH'),
	path: z.literal('/v0/tee-time-alerts/{id}/toggle'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			id: z.string(),
		}),
	}),
	responses: z.object({
		'200': TeeTimeAlert,
	}),
};

export type put_DevicesController_updateDevice_v0 = typeof put_DevicesController_updateDevice_v0;
export const put_DevicesController_updateDevice_v0 = {
	method: z.literal('PUT'),
	path: z.literal('/v0/devices/{deviceId}'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		path: z.object({
			deviceId: z.string(),
		}),
		body: UpdateDeviceInput,
	}),
	responses: z.object({
		'200': Device,
	}),
};

export type post_SupportController_createSupportRequest_v0 = typeof post_SupportController_createSupportRequest_v0;
export const post_SupportController_createSupportRequest_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/support/request'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: CreateSupportRequest,
	}),
	responses: z.object({
		'200': EmptyResponse,
	}),
};

export type post_SupportController_troonPriveRequest_v0 = typeof post_SupportController_troonPriveRequest_v0;
export const post_SupportController_troonPriveRequest_v0 = {
	method: z.literal('POST'),
	path: z.literal('/v0/support/troon-prive-request'),
	requestFormat: z.literal('json'),
	parameters: z.object({
		body: TroonPriveRequest,
	}),
	responses: z.object({
		'200': EmptyResponse,
	}),
};

// <EndpointByMethod>
export const EndpointByMethod = {
	post: {
		'/v0/auth/get-web-session': post_AuthenticationController_getWebSession_v0,
		'/v0/auth/login': post_AuthenticationController_login_v0,
		'/v0/auth/signup': post_AuthenticationController_restSignup_v0,
		'/v0/auth/refresh-token': post_AuthenticationController_refreshToken_v0,
		'/v0/auth/magic-link/email': post_AuthenticationController_emailMagicLink_v0,
		'/v0/auth/magic-link/confirm': post_AuthenticationController_confirmEmailMagicLink_v0,
		'/v0/auth/forgot-password': post_AuthenticationController_forgotPassword_v0,
		'/v0/auth/reset-password': post_AuthenticationController_resetPassword_v0,
		'/v0/auth/logout': post_AuthenticationController_logout_v0,
		'/v0/access-rewards/conversion-confirm': post_AccessRewardsController_confirmConversion_v0,
		'/v0/rewards/programs/{programSignUpType}': post_ProgramsController_enrollSignupProgram_v0,
		'/v0/subscription-addons/preview': post_SubscriptionAddonController_previewAddon_v0,
		'/v0/subscription-addons/purchase': post_SubscriptionAddonController_purchaseAddon_v0,
		'/v0/troon-access/payment-methods': post_TroonAccessController_addPaymentMethod_v0,
		'/v0/troon-access-subscriptions/setup': post_TroonAccessSubscriptionsController_setupSubscription_v0,
		'/v0/troon-access-subscriptions/confirm': post_TroonAccessSubscriptionsController_confirmSubscriptionPayment_v0,
		'/v0/troon-access-subscriptions/upgrade/preview': post_TroonAccessSubscriptionsController_previewUpgrade_v0,
		'/v0/troon-access-subscriptions/upgrade/confirm': post_TroonAccessSubscriptionsController_confirmUpgrade_v0,
		'/v0/visa/tokens': post_VisaController_getVisaToken_v0,
		'/v0/visa/prive/requests': post_VisaController_createVisaPriveRequest_v0,
		'/v0/users/account/phone': post_UsersController_updatePhone_v0,
		'/v0/users/account/phone/confirm': post_UsersController_confirmPhone_v0,
		'/v0/users/account/waiver': post_UsersController_signWaiver_v0,
		'/v0/users/account/data-sharing-consent': post_UsersController_updateDataSharingConsent_v0,
		'/v0/facilities/toggle-favorite': post_FacilitiesController_toggleFavoriteFacility_v0,
		'/v0/payments/token': post_PaymentsController_createPaymentToken_v0,
		'/v0/payments/intent': post_PaymentsController_createStripeSetupIntent_v0,
		'/v0/payments/credit-cards': post_PaymentsController_addCreditCard_v0,
		'/v1/payments/credit-cards': post_PaymentsController_addCreditCardV1_v1,
		'/v0/reservations': post_ReservationsController_reserveTeeTime_v0,
		'/v1/reservations/{reservationId}/users/{reservationUserId}/payment-option':
			post_ReservationsController_submitPostBookingPaymentOption_v1,
		'/v0/reservations/{reservationId}/join/{shareInviteId}': post_ReservationsController_joinReservation_v0,
		'/v0/reservations/{reservationId}/invites/{reservationUserId}':
			post_ReservationsController_inviteUserToReservation_v0,
		'/v0/reservations/{reservationId}/users/{reservationUserId}/guest-pass':
			post_ReservationsController_addGuestPassToUser_v0,
		'/v0/shopify/transfer': post_ShopifyController_transferToStoreCredit_v0,
		'/v0/shopify/sync-customer-by-email': post_ShopifyController_syncCustomerByEmail_v0,
		'/v0/tee-time-alerts': post_TeeTimeAlertsController_createFacilityTeeTimeAlert_v0,
		'/v0/support/request': post_SupportController_createSupportRequest_v0,
		'/v0/support/troon-prive-request': post_SupportController_troonPriveRequest_v0,
	},
	get: {
		'/v0/auth/me': get_AuthenticationController_me_v0,
		'/v0/access-rewards/conversion-preview': get_AccessRewardsController_getConversionPreview_v0,
		'/v0/access-rewards/transactions': get_AccessRewardsController_getUserTransactions_v0,
		'/v0/rewards/programs/{programSignUpType}': get_ProgramsController_getSignupProgram_v0,
		'/v0/subscription-addons/products': get_SubscriptionAddonController_getAddonProducts_v0,
		'/v0/tee-times': get_TeeTimesController_getCourseTeeTimes_v0,
		'/v1/tee-times': get_TeeTimesController_getCourseTeeTimesV1_v1,
		'/v0/tee-times/{teeTimeId}': get_TeeTimesController_getTeeTimeReservationInfo_v0,
		'/v1/tee-times/{teeTimeId}': get_TeeTimesController_getTeeTimeReservationInfoV1_v1,
		'/v0/troon-access/payment-methods': get_TroonAccessController_getPaymentMethods_v0,
		'/v0/troon-access-savings/reservations/{reservationId}/upsell':
			get_TroonAccessSavingsController_getReservationUpsell_v0,
		'/v0/troon-access-savings/total': get_TroonAccessSavingsController_getTotalSavings_v0,
		'/v0/troon-access-savings/discounts-feed': get_TroonAccessSavingsController_getDiscountsFeed_v0,
		'/v0/troon-access-savings/access-premium-tee-time-deals':
			get_TroonAccessSavingsController_getAccessPremiumTeeTimeDeals_v0,
		'/v0/troon-access-subscriptions/active': get_TroonAccessSubscriptionsController_getActiveSubscription_v0,
		'/v0/troon-access-subscriptions/products': get_TroonAccessSubscriptionsController_getProducts_v0,
		'/v0/troon-access-subscriptions/products/{id}': get_TroonAccessSubscriptionsController_getProduct_v0,
		'/v0/visa/status': get_VisaController_getVisaStatus_v0,
		'/v0/visa/tokens/{token}': get_VisaController_verifyVisaRequest_v0,
		'/v0/visa/prive/requests': get_VisaController_getPriveRequests_v0,
		'/v0/users/exists': get_UsersController_userExists_v0,
		'/v0/users/email-exists': get_UsersController_userEmailExists_v0,
		'/v0/users/account': get_UsersController_getUserAccount_v0,
		'/v1/users/account': get_UsersController_getUserAccountV1_v1,
		'/v0/users/account/waiver': get_UsersController_waiver_v0,
		'/v0/users/account/settings': get_UsersController_getUserSettings_v0,
		'/v0/users/account/data-sharing-consent': get_UsersController_getDataSharingConsent_v0,
		'/v0/facilities': get_FacilitiesController_getFacilities_v0,
		'/v0/facilities/nearby': get_FacilitiesController_getNearbyFacilities_v0,
		'/v0/facilities/favorites': get_FacilitiesController_getFavoriteFacilities_v0,
		'/v0/facilities/{idOrSlug}': get_FacilitiesController_getFacility_v0,
		'/v0/guest-passes': get_GuestPassesController_getGuestPasses_v0,
		'/v0/site-embeds/{id}': get_SiteEmbedsController_getEmbed_v0,
		'/v0/payments/credit-cards': get_PaymentsController_getCreditCards_v0,
		'/v0/reservations/invites': get_ReservationsController_getReservationInvites_v0,
		'/v0/reservations/{id}': get_ReservationsController_getReservationById_v0,
		'/v0/reservations': get_ReservationsController_getAllReservations_v0,
		'/v0/reservations/{id}/payment-info': get_ReservationsController_getReservationPaymentInfo_v0,
		'/v1/reservations/{id}/payment-info': get_ReservationsController_getReservationPaymentInfoV1_v1,
		'/v1/reservations/{reservationId}/users/{reservationUserId}/redemption-preview':
			get_ReservationsController_previewPostBookingRedemption_v1,
		'/v0/reservations/{reservationId}/join/{shareInviteId}': get_ReservationsController_getReservationInvite_v0,
		'/v0/reservations/{reservationId}/suggested-users': get_ReservationsController_getSuggestedReservationUsers_v0,
		'/v0/search': get_SearchController_search_v0,
		'/v0/search/popular-destinations': get_SearchController_popularDestinations_v0,
		'/v0/local-cards': get_LocalCardsController_getMyLocalCards_v0,
		'/v0/shopify/transfer-options': get_ShopifyController_getTransferOptions_v0,
		'/v0/tee-time-alerts': get_TeeTimeAlertsController_getTeeTimeAlerts_v0,
		'/v0/tee-time-alerts/{id}': get_TeeTimeAlertsController_getTeeTimeAlert_v0,
	},
	patch: {
		'/v0/auth/me': patch_AuthenticationController_updateMe_v0,
		'/v0/users/account/settings': patch_UsersController_updateUserSettings_v0,
		'/v0/reservations/{reservationId}/invites/{reservationUserId}':
			patch_ReservationsController_acceptReservationInvite_v0,
		'/v0/tee-time-alerts/{id}': patch_TeeTimeAlertsController_updateFacilityTeeTimeAlert_v0,
		'/v0/tee-time-alerts/{id}/toggle': patch_TeeTimeAlertsController_toggleTeeTimeAlert_v0,
	},
	delete: {
		'/v0/users/account': delete_UsersController_deleteAccount_v0,
		'/v0/payments/credit-cards/{id}': delete_PaymentsController_deleteCreditCard_v0,
		'/v0/reservations/{reservationId}': delete_ReservationsController_cancelReservation_v0,
		'/v0/reservations/{reservationId}/invites/{reservationUserId}':
			delete_ReservationsController_rejectReservationInvite_v0,
		'/v0/reservations/{reservationId}/users/{reservationUserId}': delete_ReservationsController_leaveReservation_v0,
		'/v0/reservations/{reservationId}/users/{reservationUserId}/remove':
			delete_ReservationsController_removeUserFromReservation_v0,
		'/v0/reservations/{reservationId}/guest-passes/{guestPassId}': delete_ReservationsController_deactivateGuestPass_v0,
		'/v0/tee-time-alerts/{id}': delete_TeeTimeAlertsController_deleteTeeTimeAlert_v0,
	},
	put: {
		'/v0/devices/{deviceId}': put_DevicesController_updateDevice_v0,
	},
};
export type EndpointByMethod = typeof EndpointByMethod;
// </EndpointByMethod>

// <EndpointByMethod.Shorthands>
export type PostEndpoints = EndpointByMethod['post'];
export type GetEndpoints = EndpointByMethod['get'];
export type PatchEndpoints = EndpointByMethod['patch'];
export type DeleteEndpoints = EndpointByMethod['delete'];
export type PutEndpoints = EndpointByMethod['put'];
// </EndpointByMethod.Shorthands>
